import { Component, HostListener, ChangeDetectorRef, ViewChild, ChangeDetectionStrategy, Injector, ElementRef, OnDestroy } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { CsPage, NavController } from 'providers/nav-controller';
import { SettingsService } from 'services/settings.service';
import { SvPlanHandlerService, FakePlanParams } from 'services/server/sv-plan-handler.service';
import { getMyApp } from 'app/app-interface';
import { UserDb } from 'services/user-db.service';
import { ScrollHideService } from 'services/scroll-hide.service';
import {
  DeclaredStrength_Slot,
  ExerciseStat,
  isUsersTrainingGoalGrowth,
  getRecommendedGoalSwitch,
  LongerFinger,
  getNutritionOptionsDescription,
  NutritionCoachingType,
  NutritionTrackingType,
  FitnessState,
  MacroTargetsType,
  DeclaredState,
  ImmutableState,
  getWorkoutsPerWeek,
} from '@cf-shared/data-model/user-state';
import { calculateBF } from '@cf-shared/data-model/bodyfat';
import { TrainingSplitMode, resolveTrainingSplitMode } from '@cf-shared/data-model/training-split';
import { getMinimumFrequencyForSplitMode, resolveEffectiveSplitMode } from '@cf-shared/workout-split-policy';
import { bfErrorTexts } from 'util/formatting';
import { DatabaseWrapper, DocWatcher } from 'services/database-wrapper';
import { Gender, BFMethod, ActivityLevel, SleepQuality, StressLevel, TrainingGoal, AdvancedMethodsMyoReps, DedicationLevel, AdvancedMethodsRMTesting, MassUnit } from '@cf-shared/enums';
import { TrainingLevel, averageTrainingLevels } from '@cf-shared/data-model/training-level';
import { ExerciseDbService, Exercise, isStrengthStandardRelative, StrengthPerBW, StrengthClass, ExerciseSlot } from 'services/exercise-db.service';
import { DateTime } from 'luxon';
import { ChangeChecker } from 'services/change-checker';
import { deepClone, deepCompareEQ, deepOverwrite, deepOverwriteCouchSafe } from '@cf-shared/deep-helpers';
import { DebugService } from 'services/debug.service';
import { MuscleGrowthMain, OneMuscleGrowthValue } from '@cf-shared/data-model/muscle-growth-preference';
import { estimate1RM } from '@cf-shared/data-model/set-stats';
import { IntensityLevel, FitnessPlan, WelcomePhase, hasHandEditsSinceProgramWasBuilt } from '@cf-shared/data-model/fitness-plan';
import {
  IntakeFormState,
  getTrainingVolumeForDedicationLevel,
  getSelectedTrainingDedicationLevel,
  getSuggestedTrainingDedicationLevel,
} from '@cf-shared/data-model/intake-form-state';
import { ScaledImage } from '@cf-shared/data-model/scaled-image';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { ToastController } from 'providers/toast-controller';
import { ToastType } from 'components/toast/toast-host';
import { matches } from 'lodash-es';
import { roundToPrecision } from '@cf-shared/shared-util';
import { constraintIntakeDeltaPercentForDedicationLevel, isIntakeDeltaCompatibleWithProgram } from '@cf-shared/intake-delta';
import '@cf-shared/util/array-sequential-equal';
import { readUsersOwnCalorieTarget } from '@cf-shared/plan-update-shared';
import { applySavedStateToIntakeForm, mapIntakeFormToDeclaredState, mapIntakeFormToImmutableState, getSelectedNutritionDedicationLevel } from '@cf-shared/data-model/intake-form-helpers';
import { LoggingService } from 'services/logging.service';
import { ScheduleRule, adjustScheduleToDeclaredState, getNextWorkoutDateISO } from '@cf-shared/data-model/schedule';
import { TelemetryService } from 'services/telemetry.service';
import { NotificationService } from 'services/notification.service';
import { isCapacitorNative } from 'environments/environment-helpers';
import { CsSliderBackgroundGradientMark, CsSliderTickmark } from 'components/cs-slider/cs-slider';
import { IEditWorkoutsPage, EditWorkoutsPageResult } from 'pages/edit-workouts/edit-workouts-interface';
import { LoadingController } from 'providers/loading-controller';
import { BodyPartId, lowerBodyParts, upperBodyParts } from '@cf-shared/fitness-constants';
import { getBodyPartLabel } from 'util/muscle-labels';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { buildMuscleVolumeInputsAsync } from 'util/muscle-volume-helpers';
import { TrainingProgramData, ProgramOverviewData, WorkoutLoaderService } from 'services/workout-loader.service';
import { VldProgressDirective } from 'directives/vld-progress/vld-progress';
import { VldGroupState } from 'directives/vld-group/vld-group-interface';
import { waitHtmlEvent, waitForAngularUpdate } from 'services/browser-utils';
import { ISettingsPage } from 'pages/settings-page/settings-page-interface';
import { HomeTab, IHomePage } from 'pages/home/home-interface';
import { IProgramSettingsPage } from 'pages/program-settings-page/program-settings-interface';
import { decideIntakeLeaveTarget, IntakeLeaveTarget } from './intake-leave';
import { Config_Kilograms, Config_Pounds } from '@cf-shared/measure-configs';
import { IEditNutritionPlanPage, EditNutritionPlanPageResult } from 'pages/edit-nutrition-plan/edit-nutrition-plan-interface';
import {
  IUpdatePlanOptionsPage,
  NutritionUpdateChoice,
  UpdatePlanOptionsPageResult,
  WorkoutsUpdateChoice,
} from 'pages/update-plan-options/update-plan-options-interface';
import { computeNutritionPlanData, computeEditNutritionPlanMacros, computeRecommendedNutrition } from 'components/cs-nutrition-plan-card/nutrition-plan-compute';
import { CoachClientSlot } from '@cf-shared/data-model/coach';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { isDatabaseTornDown } from 'services/database-unavailable-error';
import { CoachNotifyService } from 'services/coach-notify.service';
import { AuthService } from 'services/auth.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { coachBannerEvent, PubSubService } from 'services/pubsub.service';
import { displayedIntakeStepTotal, IntakeFormPageParams, IntakeStep, IntakeStepUrl } from './intake-form-interface';
import { choosePlanToPersistAtFinish, describePlanForReport } from './intake-finish-plan';
import { decideOverviewRedraw, maxEffortSnapshotMatchesForm, OverviewRedraw, SnapshotComparison } from './intake-plan-validity';
import { prepareStrengthStepAsync, strengthLoadBlocksNext, StrengthTestingUI_Exercise, StrengthTestingUI_Slot, StrengthTestsLoad } from './intake-strength-testing';
import { chooseFitnessStateToPersistAtFinish } from './intake-finish-fitness-state';
import { ISubscribePage } from 'pages/subscribe/subscribe-interface';
import { isWorkoutSolverRefused } from 'services/server/workout-solver-failure';
import { IWorkoutDetailPage } from 'pages/workout-detail/workout-detail-interface';
import { WakeLockService } from 'services/wake-lock.service';

// Conversion functions between numeric IntakeStep and string IntakeStepUrl
export function intakeStepToUrl(step: IntakeStep): IntakeStepUrl {
  const mapping: Record<IntakeStep, IntakeStepUrl> = {
    [IntakeStep.BasicInfo]: IntakeStepUrl.BasicInfo,
    [IntakeStep.BodyComposition]: IntakeStepUrl.BodyComposition,
    [IntakeStep.Strength]: IntakeStepUrl.Strength,
    [IntakeStep.Lifestyle]: IntakeStepUrl.Lifestyle,
    [IntakeStep.AdvancedTraining]: IntakeStepUrl.AdvancedTraining,
    [IntakeStep.Schedule]: IntakeStepUrl.Schedule,
    [IntakeStep.NutritionOptions]: IntakeStepUrl.NutritionOptions,
    [IntakeStep.Goals]: IntakeStepUrl.Goals,
    [IntakeStep.TrainingVolume]: IntakeStepUrl.TrainingVolume,
    [IntakeStep.EnergyBalance]: IntakeStepUrl.EnergyBalance,
    [IntakeStep.ProgramOverview]: IntakeStepUrl.ProgramOverview,
    [IntakeStep.FirstWorkoutHandoff]: IntakeStepUrl.FirstWorkoutHandoff
  };
  return mapping[step] || IntakeStepUrl.BodyComposition;
}

export function urlToIntakeStep(urlStep: IntakeStepUrl | string): IntakeStep {
  const mapping: Record<string, IntakeStep> = {
    [IntakeStepUrl.BasicInfo]: IntakeStep.BasicInfo,
    [IntakeStepUrl.BodyComposition]: IntakeStep.BodyComposition,
    [IntakeStepUrl.Strength]: IntakeStep.Strength,
    [IntakeStepUrl.Lifestyle]: IntakeStep.Lifestyle,
    [IntakeStepUrl.AdvancedTraining]: IntakeStep.AdvancedTraining,
    [IntakeStepUrl.Schedule]: IntakeStep.Schedule,
    [IntakeStepUrl.NutritionOptions]: IntakeStep.NutritionOptions,
    [IntakeStepUrl.Goals]: IntakeStep.Goals,
    [IntakeStepUrl.TrainingVolume]: IntakeStep.TrainingVolume,
    [IntakeStepUrl.EnergyBalance]: IntakeStep.EnergyBalance,
    [IntakeStepUrl.ProgramOverview]: IntakeStep.ProgramOverview,
    [IntakeStepUrl.FirstWorkoutHandoff]: IntakeStep.FirstWorkoutHandoff
  };
  return mapping[urlStep] || IntakeStep.BodyComposition;
}

enum IntakeView {
  Tabs = 'tabs',
  ProgramOverview = 'program-overview'
}

@Component({
  selector: 'page-intake-form',
  templateUrl: 'intake-form.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IntakeFormPage extends CsPage<IntakeFormPageParams> implements OnDestroy {
  protected LL = LL;
  @ViewChild(VldProgressDirective) vldProgress: VldProgressDirective;
  @ViewChild('trainingVolumeBadgeValue') trainingVolumeBadgeValue: ElementRef;
  @ViewChild('intakeDeltaBadgeValue') intakeDeltaBadgeValue: ElementRef;

  Math = Math;

  // how many progress steps are there in the validation "wizard"
  totalSteps: number;

  // the step total the header and stepper show: 8 for a client filling a coached intake (#6793)
  displayedTotalSteps(): number {
    return displayedIntakeStepTotal(this.totalSteps, this.isCoachedOnboarding());
  }

  // these are needed for enum binding in template
  IntakeStep = IntakeStep;
  IntakeView = IntakeView;
  StrengthTestsLoad = StrengthTestsLoad;
  Gender = Gender;
  LongerFinger = LongerFinger;
  BFMethod = BFMethod;
  ActivityLevel = ActivityLevel;
  SleepQuality = SleepQuality;
  StressLevel = StressLevel;
  OneMuscleGrowthValue = OneMuscleGrowthValue;
  MuscleGrowthMain = MuscleGrowthMain;
  TrainingLevel = TrainingLevel;
  IntensityLevel = IntensityLevel;
  TrainingGoal = TrainingGoal;
  AdvancedMethodsMyoReps = AdvancedMethodsMyoReps;
  AdvancedMethodsRMTesting = AdvancedMethodsRMTesting;
  possibleTrainingFrequency = [1, 2, 3, 4, 5, 6, 7];

  // Tab mode state
  private _selectedTab: IntakeStep = IntakeStep.BodyComposition;
  get selectedTab(): IntakeStep {
    return this._selectedTab;
  }
  set selectedTab(value: IntakeStep) {
    const previousTab = this._selectedTab;
    this._selectedTab = value;
    // clear notification dot when user visits the tab
    if (value === IntakeStep.TrainingVolume) {
      this.showTrainingVolumeNotification = false;
    }
    if (value === IntakeStep.EnergyBalance) {
      this.showEnergyBalanceNotification = false;
    }
    // URL updating is now handled by cs-tab component

    // if in edit mode and tab changed, track tab open event
    if (this.mode !== 'new' && value !== previousTab) {
      this.trackTabOpenEvent(value);
    }
  }

  private trackTabOpenEvent(step: IntakeStep) {
    const stepNameMap: Partial<Record<IntakeStep, string>> = {
      [IntakeStep.BodyComposition]: 'body_composition',
      [IntakeStep.Lifestyle]: 'lifestyle',
      [IntakeStep.Schedule]: 'schedule',
      [IntakeStep.Goals]: 'goals',
      [IntakeStep.TrainingVolume]: 'training_volume',
      [IntakeStep.EnergyBalance]: 'energy_balance',
    };
    const stepName = stepNameMap[step];
    if (stepName) {
      this.trackIntakeStepEvent(`intake_form_${stepName}_open`);
    }
  }

  currentView: IntakeView = IntakeView.Tabs;

  // tracks if page has been initialized (to skip re-initialization when returning from child pages)
  private initialized = false;

  // Red dot indicator for the Training Volume tab when its suggestion changed during
  // recalculation.
  showTrainingVolumeNotification: boolean = false;

  // Red dot indicator for the Energy Balance tab. #3611 normally preserves the user's
  // chosen surplus/deficit across a recalc, but when the goal direction flips (e.g.
  // fat loss → muscle growth) the saved value is no longer valid and is replaced by
  // the new suggestion — this dot tells the user to review it (#4037).
  showEnergyBalanceNotification: boolean = false;

  // URL conversion functions for cs-tab component
  intakeStepToUrl = intakeStepToUrl;
  urlToIntakeStep = urlToIntakeStep;

  // Single source of truth for step titles (translated)
  get STEP_TITLES(): Record<IntakeStep, TranslatedString> {
    return {
      [IntakeStep.BasicInfo]: LL.intake.steps.basicInfo(),
      [IntakeStep.BodyComposition]: LL.intake.steps.bodyComposition(),
      [IntakeStep.Strength]: LL.intake.steps.strength(),
      [IntakeStep.Lifestyle]: LL.intake.steps.lifestyle(),
      [IntakeStep.AdvancedTraining]: LL.intake.steps.advancedTraining(),
      [IntakeStep.Schedule]: LL.intake.steps.schedule(),
      [IntakeStep.NutritionOptions]: LL.intake.steps.nutritionOptions(),
      [IntakeStep.Goals]: LL.intake.steps.goals(),
      [IntakeStep.TrainingVolume]: LL.intake.steps.trainingVolume(),
      [IntakeStep.EnergyBalance]: LL.intake.steps.energyBalance(),
      [IntakeStep.ProgramOverview]: LL.intake.steps.programOverview(),
      [IntakeStep.FirstWorkoutHandoff]: LL.intake.steps.firstWorkoutHandoff()
    };
  }

  NutritionCoachingType = NutritionCoachingType;

  trainingVolumeSliderTrackBackgroundGradientMarks: CsSliderBackgroundGradientMark[] = [];
  intakeDeltaPercentSliderTrackBackgroundGradientMarks: CsSliderBackgroundGradientMark[] = [];
  trainingVolumeSliderHighlightedValues: number[] = [];
  intakeDeltaPercentSliderHighlightedValues: number[] = [];

  roundToPrecision = roundToPrecision;

  hasClientSmallScreen = hasClientSmallScreen;

  intakeDeltaPercentTickmarks: CsSliderTickmark[] = [
    { value: -50, label: '-50%' },
    { value: -40, label: '-40%' },
    { value: -30, label: '-30%' },
    { value: -20, label: '-20%' },
    { value: -10, label: '-10%' },
    { value: 0, label: '0%' },
    { value: 10, label: '10%' },
    { value: 20, label: '20%' },
    { value: 30, label: '30%' },
    { value: 40, label: '40%' },
    { value: 50, label: '50%' },
  ]

  trainingVolumeWeeklyAvgTickmarks: CsSliderTickmark[] = [
    { value: 1, label: '1' },
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 15, label: '15' },
    { value: 20, label: '20' },
    { value: 25, label: '25' },
    { value: 30, label: '30' },
    { value: 35, label: '35' },
    { value: 40, label: '40' },
    { value: 45, label: '45' },
    { value: 50, label: '50' },
  ];

  intakeFormState: IntakeFormState = {} as IntakeFormState;
  intakeFormState_changed: ChangeChecker;
  nutritionCoachingOn = true; // boolean mirrors as the toggle cannot use enums
  nutritionTrackingOn = true;
  macroTargetsFlexible = true;
  nutritionOptionsDescription = '';
  recommendations = {
    ideal: false,
    trainingFrequencyText: '',
    trainingDaysText: '',
    trainingFrequencyGreat: false,
    mealFrequencyText: '',
    sleepText: '',
    sleepTextType: '' as 'success-strong' | 'info' | '',
    advancedMethodsMyoRepsText: '',
    advancedMethodsRMTestingTexts: '',
  };
  datePlaceholder: string;

  lowerBodyParts = lowerBodyParts;
  upperBodyParts = upperBodyParts;

  // the body-part display names are translated UI text now (#4210 Phase E), so
  // the template asks for them by id instead of reading a name off the constant
  protected bodyPartLabel(bodyPartId: BodyPartId): string {
    return getBodyPartLabel(bodyPartId);
  }

  protected trainingData: TrainingProgramData;
  protected programOverview: ProgramOverviewData;
  protected selectedWorkout: string = '';

  protected nutrition: {
    unavailable: string;
    expanded: boolean;
    dailyAvg: string;
    cutMeal: string;
    bulkMeal: string;
    averageMeal: string;
    protein: string;
    carbs: string;
    fat: string;
    fiber: string;
  } = { expanded: false } as any;

  strengthTestingSlots: StrengthTestingUI_Slot[];
  strengthTestingExercises: Exercise[];
  /** whether the Strength step's exercises could be read; while they could not, the step cannot be passed (#6540) */
  strengthTestsLoad: StrengthTestsLoad = StrengthTestsLoad.Loaded;

  loading = true;
  nextButtonId = '';

  // #4059 first-workout handoff (step 12, new end users only). true when a workout
  // is scheduled within the next week so "Start your first workout" can launch a
  // session today (reusing the rest-day launch path). computed when advancing to the
  // handoff step; when false the Start button is hidden so the user is never stuck.
  protected hasStartableFirstWorkout = false;
  // #4059: true when the first scheduled workout falls on today (vs a future day).
  // only used to tag the telemetry event; the Start button launches today either way.
  private isFirstScheduledWorkoutToday = false;

  mode: 'new' | 'change' | null = null;
  isCoachMode = false;
  private coachClientSlot: CoachClientSlot;

  frontImage: ScaledImage = null;
  backImage: ScaledImage = null;
  additionalImages = new Array<ScaledImage>();
  sleepDuration: number;
  queueClearingIntakeFormState: boolean = false;
  // #4059: set while THIS device is running finishIntakeForm. The local plan save sets
  // fitnessPlan.ready, which fires the DocWatcher's cross-device auto-exit to Home — that
  // would race with (and clobber) finishIntakeForm's own navigation into the first workout.
  // The flag tells the watcher to stand down; finishIntakeForm owns navigation on finish.
  private finishingIntake: boolean = false;
  protected skipPlanRegeneration: boolean = false;
  // #3382 / #3635: set when a keep/volumes-only change re-solved the plan's nutrition
  // targets for a changed nutrition input (meal frequency or energy balance) — forces
  // finishIntakeForm to persist the otherwise skipped plan so the new target takes effect.
  private nutritionResolvedForKeptPlan: boolean = false;
  // df-919e69164e24: set when the user edited the program from the overview — workouts via
  // Edit Workouts, or the daily calorie / macro overrides via the nutrition card. Answering
  // "keep my program" or "update volumes only" means "do not REBUILD my program", never "and
  // throw away anything I change afterwards" — but the finish reads it as "write no plan at
  // all", so without this flag every such edit was silently discarded. Set only when the edit
  // really changed the program, so an editor opened and closed again still writes nothing.
  private planEditedForKeptPlan: boolean = false;
  // df-5556257f1229: the fitness state as it stood before the user's FIRST Edit Workouts
  // visit from the program summary. Undefined until they open that page, which is also how
  // the finish knows there is no edit to carry. It is the shared starting point the finish
  // compares against, so it can tell what the VISIT changed from what the intake answers
  // changed — see chooseFitnessStateToPersistAtFinish in intake-finish-fitness-state.ts.
  //
  // Taken once and not per visit, deliberately. After the first visit `fitnessState` IS the
  // copy that page handed back, so a fresh snapshot on the second visit would make the first
  // visit's edits look like part of the starting point, and they would never be stored.
  private fitnessStateBeforeEditWorkouts: FitnessState = undefined;

  private docWatcher: DocWatcher;
  private remoteUpdatePending: boolean = false;
  private fitnessPlan: FitnessPlan;
  private localIntakeStep: number;

  private lastBFMethod: BFMethod = undefined;
  private lastFitnessStateBf_pc: number;
  private lastTrainingGoalGrowth: TrainingGoal | boolean;

  private fitnessState: FitnessState = undefined;
  protected generatedFitnessPlan: FitnessPlan;
  private generatedPlanIntakeFormState: IntakeFormState;

  currentTrainingVolumeWeeklyAvg: number = undefined;
  currentIntakeDeltaPercent: number = undefined;

  // workout split (#4591): exposed so the template can name the modes
  protected TrainingSplitMode = TrainingSplitMode;

  constructor(
    public navCtrl: NavController,
    public settings: SettingsService,
    private userDb: UserDb,
    public svPlanHandler: SvPlanHandlerService,
    private loadingCtrl: LoadingController,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private exerciseDb: ExerciseDbService,
    private changeDetector: ChangeDetectorRef,
    private debug: DebugService,
    public logging: LoggingService,
    private telemetry: TelemetryService,
    
    private scrollHideService: ScrollHideService,
    private injector: Injector,
    private workoutLoader: WorkoutLoaderService,
    private pubSub: PubSubService,
    private notificationService: NotificationService,
    private wakeLock: WakeLockService,
    private coachNotify: CoachNotifyService,
    private auth: AuthService,
    private cloudFunctions: CloudFunctionsService,
  ) {
    super();
  }

  // option B coach mode (#3389): the coach is impersonating a client
  // (entered via setCoachManagingClient). Used to relax change-mode
  // gates so the coach can review every answer the client filled.
  // Public so the template can gate the BasicInfo + Strength tabs.
  isCoachManaging(): boolean {
    return !!this.userDb.getCoachManagingClient();
  }

  // option B coach onboarding (#3389). Returns true when the client (NOT
  // the coach impersonating them) is filling the coached intake — the
  // wizard runs steps 1-8 only and the submit-and-hold path takes over
  // when they try to advance past Goals.
  private isCoachedOnboarding(): boolean {
    return !this.isCoachMode
      && !this.isCoachManaging()
      && this.fitnessPlan?.welcomePhase === WelcomePhase.CoachedIntake;
  }

  // option B coach review (#3389). True when the coach is impersonating
  // a client who has submitted their intake and is waiting for
  // finalization. Drives the change-mode (tabbed) forcing relaxation so
  // the coach can jump between answers.
  //
  // NOTE: the stall-fallback case (welcomePhase === CoachedIntake +
  // coach impersonation) is intentionally NOT included here. In stall
  // fallback the intake has not been filled at all, so declaredState
  // singletons (e.g. trainingDays) are absent. The change-mode path
  // assumes those are present and dereferences them in at least two
  // hot sites — the change-mode pre-fill that initializes
  // intakeFormState from declaredState in initializeAsync, and
  // shouldMarkPlanForInitializeBaseline below (look for `.filter` on
  // declaredState.trainingDays). For stall fallback we fall through
  // to the standard not-ready -> new forcing so the coach fills the
  // wizard linearly from scratch, the same way a brand-new user would.
  private isCoachedReview(): boolean {
    return this.isCoachManaging()
      && this.fitnessPlan?.welcomePhase === WelcomePhase.AwaitingCoachFinalize;
  }

  // Does this run produce the account's VERY FIRST program?
  //
  // True for a brand-new end user ('new' mode), and true for a coached review —
  // the coach is finalizing a plan the client has never had before, so it is a
  // first-plan run even though `mode` is 'change' for the tabbed wizard.
  //
  // Everything that is only right for a first program hangs off this one answer:
  // write the born/gender/height record (it does not exist yet), seed the new-user
  // rest-timer defaults, mark the plan as a baseline generation, and build a fresh
  // default schedule instead of adjusting an existing one. There is no existing
  // schedule to adjust — adjusting the client's empty one yields meal rules with
  // NaN times.
  //
  // NOT the same question as `wasCoachedFinalize` in performFinishIntakeAsync,
  // which is deliberately wider (it also covers the CoachedIntake phase) and is
  // captured into a local before the finish mutates welcomePhase.
  private isBuildingFirstPlan(): boolean {
    return this.mode === 'new' || this.isCoachedReview();
  }

  // Are the saved records this page can reach NOT the client's answers?
  //
  // True in two situations, for two different reasons, and both mean the same
  // thing to every caller: read the client's answers from intakeFormState, and
  // start plan generation from an empty plan rather than progressing an existing
  // one.
  //
  //  - Coach mode (isCoachMode): the singletons in this database belong to the
  //    COACH, not the client. The client's data lives on the coach's client slot.
  //    Loading declaredState here would bleed coach data into the slot (#3289).
  //  - Coached review (isCoachedReview): the client has submitted their intake but
  //    the coach has not finalized it, so the client's declared, immutable and
  //    fitness singletons DO NOT EXIST YET — they are only written at finalize.
  //    Their answers live only in intakeFormState. Reading those empty singletons
  //    wipes the submission, and the solver throws on the empty state (#4139).
  //
  // The related isCoachedOnboarding() is a DIFFERENT phase, not this one seen from
  // the other side: it is true while the client is still FILLING the coached
  // intake (WelcomePhase.CoachedIntake, nobody impersonating them), whereas a
  // coached review is the phase after they submit (WelcomePhase.AwaitingCoachFinalize,
  // with the coach impersonating them).
  private areAnswersOnlyInIntakeForm(): boolean {
    return this.isCoachMode || this.isCoachedReview();
  }

  shouldShowSettingsButton(): boolean {
    // show settings button when menu is not visible
    // menu is visible when shouldShowMenu returns a truthy value ('md' or true)
    return !getMyApp().shouldShowMenu();
  }

  onSettingsClick() {
    void this.navCtrl.setRootAsync(ISettingsPage, {});
  }

  // Helper methods for tab mode
  isNewMode(): boolean {
    return this.mode === 'new';
  }

  // #4059: the first-workout handoff (step 12) is shown only to a genuine new END USER
  // completing their own intake — never to a coach setting up a client, because the coach
  // is not the one who will train. This excludes both the coach-fills-for-client path
  // (isCoachMode) and the coach-impersonation path (isCoachManaging). When false, the
  // user finishes on Program Overview (step 11) exactly as before.
  protected isFirstWorkoutHandoffEligible(): boolean {
    return this.isNewMode() && !this.isCoachMode && !this.isCoachManaging();
  }

  needsPlanRegeneration(): boolean {
    // if in new mode, no regeneration needed
    if (this.isNewMode()) {
      return false;
    }

    // in non-new mode, maxEffortPlanIntakeFormState should always exist (generated during init)
    // check if any plan-affecting inputs changed (steps 1-8, excluding volume/energy/nutrition toggles)
    if (!this.intakeFormState.maxEffortPlanIntakeFormState) {
      // should not happen in non-new mode, but handle gracefully
      return false;
    }

    // the same comparison somethingChanged() uses to throw the generated program away; the two
    // must agree (df-b9d12c28a387), so both go through this one helper
    return !maxEffortSnapshotMatchesForm(this.intakeFormState.maxEffortPlanIntakeFormState, this.intakeFormState);
  }

  private async hasTrainingPlanCustomizations(): Promise<boolean> {
    const currentPlan = await this.userDb.loadPlanAsync(this.docWatcher);
    // the rule itself lives beside the plan model, next to the rebuild rule that makes the
    // same "training state vs. user edit" split from the other side
    return hasHandEditsSinceProgramWasBuilt(currentPlan);
  }

  get currentStep(): IntakeStep {
    if (this.isNewMode()) {
      return this.intakeFormState.intakeFormProgress;
    }
    // when in program overview view, show the program overview step
    if (this.currentView === IntakeView.ProgramOverview) {
      return IntakeStep.ProgramOverview;
    }
    return this.selectedTab;
  }

  getPageTitle(): TranslatedString {
    if (this.isNewMode()) {
      // return translated step name
      return this.STEP_TITLES[this.intakeFormState.intakeFormProgress];
    }

    if (this.currentView === IntakeView.ProgramOverview) {
      return this.STEP_TITLES[IntakeStep.ProgramOverview];
    }

    // tab view - show "Update Training Program" for all non-new modes
    return LL.intake.title.updateProgram();
  }

  async handleRecalculate(): Promise<void> {
    // save the user's chosen values before generateProgramAsync deletes them
    const savedTrainingVolume = this.intakeFormState.trainingVolumeWeeklyAvg;
    const savedIntakeDelta = this.intakeFormState.intakeDeltaPercent;

    // regenerate max effort plan with new inputs
    // generateProgramAsync(true) shows its own loader ("Analyzing fitness goals...")
    // and will automatically set trainingVolumeWeeklyAvg and intakeDeltaPercent
    // to suggested values based on the new plan
    await this.generateProgramAsync(true);

    // #3611: preserve the user's chosen calorie deficit. generateProgramAsync(true) resets
    // intakeDeltaPercent to the Balanced suggestion, but the deficit is a deliberate user
    // choice that does not depend on the input that forced the recalc (e.g. training days).
    // Restoring it here mirrors the init path and stops the "the app reset my calories" data
    // loss reported in #3382 / #3611.
    if (savedIntakeDelta !== undefined) {
      this.restoreSavedIntakeDeltaIfCompatible(savedIntakeDelta);
    }

    // re-snapshot so maxEffortPlanIntakeFormState matches the restored state, exactly as the
    // init path does after its restore. needsPlanRegeneration() ignores intakeDeltaPercent
    // today, so this is not load-bearing now, but keeping the snapshot in sync avoids a latent
    // mis-fire if that ever changes — and keeps the two regeneration paths consistent.
    // ignoreMap drops the snapshot key itself so it never nests into its own snapshot (#3405).
    this.intakeFormState.maxEffortPlanIntakeFormState = deepClone(this.intakeFormState, { ignoreMap: { maxEffortPlanIntakeFormState: true } });

    // show a red dot on the Training Volume tab if its suggestion changed. Training volume
    // is intentionally NOT preserved: weekly volume genuinely depends on the number of
    // training days, so a recalculated suggestion is surfaced for the user to review.
    if (savedTrainingVolume !== this.intakeFormState.trainingVolumeWeeklyAvg) {
      this.showTrainingVolumeNotification = true;
    }
    // the energy-balance red dot is set above only when the saved surplus/deficit could
    // not be preserved because the goal direction flipped (#4037); in every other case
    // the value is preserved and no dot is needed.

    // Note: generateProgramAsync calls somethingChanged() which triggers markForCheck(),
    // but we need to force a full change detection cycle to update child components (tabs)
    this.changeDetector.detectChanges();
  }

  // restore the user's saved energy balance (surplus/deficit) after a program regeneration,
  // but only when it still fits the regenerated program's direction (#4037) — a cutting-era
  // deficit restored into a muscle-growth program is not reachable through the slider and
  // later makes the weekly-checkup solver reject the plan. when the value is dropped, the
  // fresh suggestion stays and the Energy Balance tab gets a notification dot for review.
  // shared by handleRecalculate (#3611 restore) and the init-path restore.
  private restoreSavedIntakeDeltaIfCompatible(savedIntakeDelta: number): void {
    // if regeneration failed there is no maxEffortPlan (generateProgramAsync returned false)
    // and so no program direction to validate against — keep the pre-#4037 behavior of
    // restoring unconditionally; the next successful regeneration re-evaluates the value
    const programIntakeDelta = this.intakeFormState.maxEffortPlan?.intakeDeltaPercent;
    if (programIntakeDelta === undefined || isIntakeDeltaCompatibleWithProgram(savedIntakeDelta, programIntakeDelta)) {
      this.intakeFormState.intakeDeltaPercent = savedIntakeDelta;
    } else {
      this.showEnergyBalanceNotification = true;
    }
  }

  async handleSaveAndUpdate(): Promise<void> {
    const allValid = await this.validateAllTabs();
    if (!allValid) {
      return;
    }

    // #4139: a coached review builds the client's FIRST plan (a baseline), and new mode
    // has nothing to keep either. Both questions on the Update Your Program screen would be
    // meaningless there — "keep my current workouts" would activate the empty
    // AwaitingCoachFinalize placeholder, and there is no calorie target to keep — so skip
    // the screen and rebuild everything, exactly as the alert this replaced did.
    if (this.isNewMode() || this.isCoachedReview()) {
      await this.applyUpdateChoicesAsync({
        workouts: WorkoutsUpdateChoice.Rebuild,
        nutrition: NutritionUpdateChoice.Update,
      });
      return;
    }

    await this.askUpdateOptionsAsync();
  }

  /**
   * Opens the Update Your Program screen, which asks two separate questions: what to do
   * with the training program, and what to do with the daily calorie target (#4973).
   *
   * It replaced a single three-button alert that carried both decisions at once and never
   * mentioned nutrition, so a user answering about their workouts was silently also
   * answering about their food. The answer comes back through onUpdateOptionsChosen —
   * modal pages in this app report through a returnCallback rather than an awaited result,
   * which is why the save is split across two methods.
   */
  private async askUpdateOptionsAsync(): Promise<void> {
    // In coach mode the screen asks about the workouts only. The singletons readable from
    // here belong to the COACH, not to the client whose plan is being edited (#3289), so a
    // nutrition question would state the coach's own calorie number as "your target" and
    // warn about changes that did not happen. The answer would do nothing either way:
    // resolveNutritionForKeptPlanIfNeeded returns early in coach mode, and the rebuild path
    // seeds from an empty plan, so its nutrition is worked out from scratch regardless.
    //
    // hasTrainingPlanCustomizations() below is the one reading that still goes to the coach's
    // own plan, so the rebuild note can warn about losing exercises the CLIENT never swapped.
    // Pre-existing — the alert this screen replaced made the same call in the same place — and
    // it only picks between two wordings, so it is left as it was rather than widened into.
    if (this.isCoachMode) {
      void this.navCtrl.pushAsync(IUpdatePlanOptionsPage, {
        returnCallback: 'onUpdateOptionsChosen',
        local: {
          hasTrainingPlanCustomizations: await this.hasTrainingPlanCustomizations(),
          askAboutNutrition: false,
          hasAcceptedTarget: false,
          targetWouldChange: false,
          activityLevelChanged: false,
          energyBalanceChanged: false,
          goalDirectionChanged: false,
          bodyDataChanged: false,
        },
      });
      return;
    }

    const [appliedDeclaredState, appliedFitnessState, appliedPlan, hasCustomizations] = await Promise.all([
      this.userDb.loadDeclaredStateAsync(this.docWatcher),
      this.userDb.loadFitnessStateAsync(this.docWatcher),
      this.userDb.loadPlanAsync(this.docWatcher),
      this.hasTrainingPlanCustomizations(),
    ]);

    // Both shapes of "the target is the user's own" live in one tested helper, because reading
    // those fields directly here is what got this wrong the first time.
    const usersOwnTarget = readUsersOwnCalorieTarget(appliedPlan);
    const hasAcceptedTarget = usersOwnTarget.hasOwnTarget;

    void this.navCtrl.pushAsync(IUpdatePlanOptionsPage, {
      returnCallback: 'onUpdateOptionsChosen',
      local: {
        hasTrainingPlanCustomizations: hasCustomizations,
        askAboutNutrition: true,
        hasAcceptedTarget,
        acceptedTarget_kcal: usersOwnTarget.target_kcal,
        recommendedTarget_kcal: hasAcceptedTarget ? await this.computeRecommendedDailyIntakeAsync(appliedPlan) : undefined,
        targetWouldChange: await this.hasTargetAffectingChangeAsync(appliedDeclaredState, appliedFitnessState),
        activityLevelChanged: this.hasActivityLevelChanged(appliedDeclaredState),
        energyBalanceChanged: this.hasEnergyBalanceChanged(appliedDeclaredState),
        goalDirectionChanged: this.hasGoalDirectionChanged(appliedDeclaredState, appliedFitnessState),
        bodyDataChanged: this.hasBodyDataChanged(appliedFitnessState),
      },
    });
  }

  /**
   * Callback invoked by NavController when returning from the Update Your Program screen.
   * This is called via the returnCallback mechanism in nav-controller.ts.
   *
   * No result means the user cancelled or backed out, and nothing is saved — deliberately
   * unlike the alert this replaced, where dismissing counted as "keep my current program"
   * and went ahead with the save anyway.
   */
  async onUpdateOptionsChosen(_context: unknown, result: UpdatePlanOptionsPageResult | undefined) {
    if (result === undefined) {
      return;
    }
    await this.applyUpdateChoicesAsync(result);
  }

  /** Carries out the two answers from the Update Your Program screen. */
  private async applyUpdateChoicesAsync(choice: UpdatePlanOptionsPageResult): Promise<void> {
    this.skipPlanRegeneration = false;

    if (choice.workouts === WorkoutsUpdateChoice.Keep) {
      this.skipPlanRegeneration = true;
      // in coach mode, use the slot's existing plan (not coach's currentplan).
      // ensures coach re-editing a client's intake doesn't accidentally
      // overwrite the client's slot with the coach's own plan (#3289).
      this.generatedFitnessPlan = this.isCoachMode
        ? deepClone(this.coachClientSlot.fitnessPlan)
        : await this.userDb.loadPlanAsync(this.docWatcher);
      // #3382/#3635: keeping the program preserves the workouts, but the nutrition answer
      // still has to be carried out on the kept plan and persisted on finish.
      await this.resolveNutritionForKeptPlanIfNeeded(choice.nutrition);
      this.installGeneratedPlan(this.generatedFitnessPlan);
      // keep the displayed nutrition card in sync with the plan that will actually be
      // applied, so the overview never shows a daily total that diverges from it (#3382).
      await this.refreshNutritionDataAsync();
      void this.refreshWorkoutsList();
    } else if (choice.workouts === WorkoutsUpdateChoice.VolumesOnly) {
      this.skipPlanRegeneration = true;
      // see comment in the keep branch — read from the slot in coach mode.
      // #5381: work on a COPY, never on the live plan singleton. Two reasons, and both
      // are needed. The singleton would be mutated before the user confirms Finish, so
      // cancelling from the overview would leave the re-optimized set counts applied
      // anyway; and replaceCurrentFitnessPlanAsync at finish does
      // deepOverwriteCouchSafe(obj, obj) when its source and destination are the same
      // object, which writes nothing (the same reason resolveNutritionForKeptPlanIfNeeded
      // clones before re-solving).
      this.generatedFitnessPlan = this.isCoachMode
        ? deepClone(this.coachClientSlot.fitnessPlan)
        : deepClone(await this.userDb.loadPlanAsync(this.docWatcher));

      const { declaredState, fitnessState, immutableState } = await this.loadStatesForGenerationAsync();
      const volumeInputs = await buildMuscleVolumeInputsAsync(this.generatedFitnessPlan, declaredState, fitnessState, this.userDb, this.exerciseDb, this.docWatcher);
      // volumes-only re-optimize also calls solveWorkouts (#3366); hold the
      // screen awake for the same reason as generateProgramAsync. Acquire
      // inside the try so the finally is the single release site.
      try {
        void this.wakeLock.acquireAsync('intake:reoptimize-volume');
        const { changes, solverResults } = await this.svPlanHandler.reoptimizeVolumeAsync(
          this.generatedFitnessPlan, declaredState, fitnessState, immutableState,
          volumeInputs.muscleFactorOverrides, volumeInputs.usesMyoRepsBySubSlotId,
          volumeInputs.muscleFactorSourceSlots,
        );
        this.svPlanHandler.applyVolumeChanges(this.generatedFitnessPlan, changes);
        // #5381: store the target the solver worked to, exactly as the full rebuild does
        // (sv-plan-handler.service.ts). Without this the per-exercise set counts moved to
        // the new volume while the plan kept advertising the old one, and every surface
        // reading this field — the Program Settings row, the intake form's "current plan"
        // note, the coach's client list, the user_weekly_sets telemetry property — showed
        // a number the program no longer ran at.
        //
        // The SOLVER's number, not the slider's. They agree in the ordinary case, but the
        // solver lowers both weekly halves when the sets will not fit the per-workout caps
        // (workout-solver.ts), and this field means what the program actually holds. What
        // the user asked for is kept separately, in declaredState, by fillAndSaveDeclaredState.
        this.generatedFitnessPlan.trainingVolumeWeeklyAvg = solverResults.trainingVolumeWeeklyAvg;
        // #5381: the finish skips the plan write whenever skipPlanRegeneration is set, and
        // neither of the other two conditions holds on this path when the user keeps their
        // calorie target. Without this flag the re-optimized program was never saved — it
        // reached the database only if some later, unrelated plan write happened to flush
        // the object first, and an app reload before that lost the whole re-optimization.
        this.planEditedForKeptPlan = true;
      } finally {
        void this.wakeLock.releaseAsync('intake:reoptimize-volume');
      }

      // as on the keep path, the nutrition answer is carried out on the plan.
      // reoptimizeVolumeAsync only touches defaultVolume_sets (workout solver), not
      // nutrition; the nutrition re-solve preserves those volumes (PlanUpdate.solve
      // reads but never writes defaultVolume_sets) and persists on finish.
      await this.resolveNutritionForKeptPlanIfNeeded(choice.nutrition);
      this.installGeneratedPlan(this.generatedFitnessPlan);
      await this.refreshNutritionDataAsync();
      void this.refreshWorkoutsList();
    }
    // WorkoutsUpdateChoice.Rebuild falls through to generate the program below

    // if not skipping regeneration (or new mode), generate the program
    if (!this.skipPlanRegeneration) {
      if (!await this.generateProgramAsync(false, choice.nutrition)) return;
    }

    this.currentView = IntakeView.ProgramOverview;
    this.changeDetector.markForCheck();
    // refresh scroll-hide to account for header height change
    await waitForAngularUpdate(this.injector);
    this.scrollHideService.refresh();
  }

  handleCancelFromTabs(event: Event) {
    lockClick(event, this.leaveFormAsync());
  }

  /**
   * Close the form: back to the page beneath it, or to Program Settings when there is none.
   * The form can be the only page in the stack when a link opened it (df-0af813f77a05);
   * decideIntakeLeaveTarget says why a plain pop is wrong then.
   */
  private async leaveFormAsync(): Promise<void> {
    if (decideIntakeLeaveTarget(this.navCtrl.canGoBack()) === IntakeLeaveTarget.PopToPrevious) {
      await this.navCtrl.popAsync();
    } else {
      await this.navCtrl.setRootAsync(IProgramSettingsPage, {});
    }
  }

  async handleCancelFromOverview(): Promise<void> {
    this.skipPlanRegeneration = false;  // reset flag when canceling
    this.nutritionResolvedForKeptPlan = false;  // #3382/#3635: reset nutrition re-solve flag when canceling
    this.planEditedForKeptPlan = false;  // df-919e69164e24: canceling applies nothing, so the edit must not force a write
    // df-5556257f1229: canceling applies nothing, so the finish must carry no Edit Workouts
    // change either. df-d93d245e5332: the page's own copy is RESTORED from that same snapshot
    // rather than cleared, or the cancelled edits pre-fill the next visit — why restoring and
    // not clearing or reloading: design-docs/sync-integrity.md, "Which fitness state the
    // intake summary draws from".
    if (this.fitnessStateBeforeEditWorkouts !== undefined) {
      this.fitnessState = this.fitnessStateBeforeEditWorkouts;
    }
    this.fitnessStateBeforeEditWorkouts = undefined;
    this.currentView = IntakeView.Tabs;
    this.changeDetector.markForCheck();
    // refresh scroll-hide to account for header height change
    await waitForAngularUpdate(this.injector);
    this.scrollHideService.refresh();
  }

  handleFinishFromOverview(event: Event): void {
    lockClick(event, this.finishIntakeForm());
  }

  async validateAllTabs(): Promise<boolean> {
    // Validate all visible vld-groups by triggering onNextClicked on each
    if (!this.vldProgress || !this.vldProgress.groups) {
      return true;
    }

    // In non-new modes, automatically skip bodyfat if not provided
    if (!this.isNewMode()) {
      const hasBfData = this.intakeFormState.bfData?.bf_pc != null;
      if (!hasBfData && this.canSkipBodyfatEstimate()) {
        this.bodyfatSkipped();
      }
    }

    // Steps shown in tabs (only validate these in non-new modes).
    // For the option B coach-managing review case (#3389) extend the
    // allow-list to include BasicInfo and Strength so the coach can
    // review/overwrite EVERY answer the client filled, not just the
    // tabs the normal client-side change-mode shows.
    const tabSteps = [
      IntakeStep.BodyComposition,
      IntakeStep.Lifestyle,
      IntakeStep.Schedule,
      IntakeStep.Goals,
      IntakeStep.TrainingVolume,
      IntakeStep.EnergyBalance,
      ...(this.isCoachManaging() ? [IntakeStep.BasicInfo, IntakeStep.Strength] : []),
    ];

    // trigger validation on all groups by calling onNextClicked
    let allValid = true;
    let firstInvalidGroup = null;
    this.vldProgress.groups.forEach(group => {
      // In non-new modes, only validate groups that are shown in tabs
      if (!this.isNewMode() && !tabSteps.includes(group.stepNumber)) {
        return; // skip this group
      }

      group.onNextClicked();
      // check if group state is Ready or Done (meaning it's valid)
      const state = group.getState();
      if (state !== VldGroupState.Ready && state !== VldGroupState.Done) {
        allValid = false;
        if (!firstInvalidGroup) {
          firstInvalidGroup = group;
        }
      }
    });

    if (!allValid && firstInvalidGroup) {
      // Show toast message
      this.toastCtrl.showToast({
        message: LL.intake.validation.checkInputs(),
        type: ToastType.WarningStrong,
        duration: 3000
      });

      // Navigate to the first invalid tab based on step number
      const stepNumber = firstInvalidGroup.stepNumber;
      if (stepNumber === IntakeStep.BodyComposition) {
        this.selectedTab = IntakeStep.BodyComposition;
      } else if (stepNumber === IntakeStep.Lifestyle) {
        this.selectedTab = IntakeStep.Lifestyle;
      } else if (stepNumber === IntakeStep.Schedule) {
        this.selectedTab = IntakeStep.Schedule;
      } else if (stepNumber === IntakeStep.Goals) {
        this.selectedTab = IntakeStep.Goals;
      } else if (stepNumber === IntakeStep.TrainingVolume) {
        this.selectedTab = IntakeStep.TrainingVolume;
      } else if (stepNumber === IntakeStep.EnergyBalance) {
        this.selectedTab = IntakeStep.EnergyBalance;
      }

      this.changeDetector.markForCheck();
    }

    return allValid;
  }

  async ionViewCanEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return undefined;

        // #4358: this callback can start several expensive refreshes, and it is
        // woken by every untracked document a sync pulls. Collect whatever this
        // pass starts and hand it all back at the end, instead of `void`-ing
        // each call — the watcher uses the returned promise to keep two passes
        // from running at once. Which calls happen is unchanged; only whether
        // the watcher knows about them.
        const startedWork: Promise<void>[] = [];

        // if the fitness plan became ready via sync, the intake form was completed
        // on another device — exit the intake form on this device too. Skip this while
        // THIS device is finishing (#4059): the local finish sets fitnessPlan.ready too,
        // and finishIntakeForm owns navigation (it may push into the first workout).
        // Also skip during coached onboarding (#4125): a client who already had a
        // generated program keeps ready=true while filling the coached intake, so
        // "ready" is not a completed-elsewhere signal there. When the coach finalizes,
        // welcomePhase flips to Done via sync, isCoachedOnboarding() turns false, and
        // this exit works again as before.
        if (this.mode === 'new' && this.fitnessPlan?.ready && !this.finishingIntake && !this.isCoachedOnboarding()) {
          this.queueClearingIntakeFormState = true;
          void this.navCtrl.setRootAsync(IHomePage, { tab: HomeTab.DailyLog });
          return undefined;
        }

        this.remoteUpdatePending = true;

        // #4059: remember the step before the sync branches below move it, so we can tell
        // when a remote device advances us into the first-workout handoff (step 12)
        const priorLocalStep = this.localIntakeStep;

        const remoteStep = this.intakeFormState.intakeFormProgress;

        // sync step when max effort plan was generated (Goals → TrainingVolume)
        if (remoteStep > IntakeStep.Goals && this.localIntakeStep <= IntakeStep.Goals) {
          this.localIntakeStep = remoteStep;
          this.markVldGroupsDone(remoteStep);
          this.updateDedicationSliders();
        }
        // sync step when max effort plan was invalidated — go back to Goals
        else if (!this.intakeFormState.maxEffortPlan && this.localIntakeStep > IntakeStep.Goals) {
          this.localIntakeStep = remoteStep;
          this.generatedFitnessPlan = undefined;
        }
        // sync step when final plan was generated (EnergyBalance → ProgramOverview)
        // use the synced generatedFitnessPlan to populate ProgramOverview
        else if (remoteStep === IntakeStep.ProgramOverview && this.localIntakeStep < IntakeStep.ProgramOverview) {
          this.localIntakeStep = remoteStep;
          this.markVldGroupsDone(remoteStep);
          if (this.intakeFormState.generatedFitnessPlan) {
            this.installGeneratedPlan(this.intakeFormState.generatedFitnessPlan);
            startedWork.push(this.refreshWorkoutsList());
            startedWork.push(this.refreshNutritionDataAsync());
          }
        }
        // sync to remote step when data changes arrive on a different step
        else if (this.localIntakeStep != null && remoteStep !== this.localIntakeStep) {
          if (remoteStep > this.localIntakeStep) {
            this.markVldGroupsDone(remoteStep);
          }
          this.localIntakeStep = remoteStep;
          // backward: vld-progress ngOnChanges resets group states when step decreases
        }
        // same step — intakeFormProgress already correct from deepOverwriteCouchSafe

        // #4059: if a remote device just advanced us into the first-workout handoff,
        // recompute Start-button eligibility (it is otherwise only set on the local
        // 11->12 tap, so a device synced straight into step 12 would lose the Start CTA)
        if (this.localIntakeStep === IntakeStep.FirstWorkoutHandoff && priorLocalStep !== IntakeStep.FirstWorkoutHandoff) {
          startedWork.push(this.computeFirstWorkoutStartabilityAsync());
        }

        // on ProgramOverview or the first-workout handoff (#4059): deepOverwriteCouchSafe
        // replaces generatedFitnessPlan with a new deep clone, so re-link our local
        // reference and refresh workouts + nutrition (both steps display the plan)
        if (this.localIntakeStep >= IntakeStep.ProgramOverview && this.intakeFormState.generatedFitnessPlan) {
          this.generatedFitnessPlan = this.intakeFormState.generatedFitnessPlan;
          // #4660: this re-link is what makes the page's two copies of the program disagree,
          // and it deliberately touches only the field. It must NOT refresh
          // generatedPlanIntakeFormState: by the time this runs, intakeFormState has already
          // been overwritten from the ARRIVING document, so copying it into the snapshot
          // would put the arriving program in both copies and Finish would persist it — the
          // exact loss this ticket is about. The snapshot is the copy the finish trusts; see
          // intake-finish-plan.ts.
          startedWork.push(this.refreshWorkoutsList());
          startedWork.push(this.refreshNutritionDataAsync());
        }

        this.changeDetector.markForCheck();
        return Promise.all(startedWork).then(() => undefined);
      }, { label: 'intake-form' });
    }

    // coach mode: filling intake on behalf of a client
    this.isCoachMode = this.navParams.data.coachClientSlotId != null;
    if (this.isCoachMode) {
      this.coachClientSlot = await this.userDb.database.getRecordAsync(this.navParams.data.coachClientSlotId, this.docWatcher) as CoachClientSlot;
      this.pubSub.publish(coachBannerEvent, { clientDisplayName: this.coachClientSlot.nickname });
    }

    // safely parse the mode parameter (have to handle the case where it can be undefined)
    let mode = this.navParams.data.mode;
    if (!['new', 'change'].includes(mode)) {
      mode = 'new';
    }
    const today = this.debug.getNowLocal();
    this.mode = mode;
    this.datePlaceholder = DateTime.fromObject({ month: 1, day: 1, year: today.year - 18 }).toISODate();

    // Tab selection from URL is now handled by cs-tab component

    this.changeDetector.markForCheck();
  }

  async ngOnInit() {
    // initialization moved to ionViewWillEnter
  }

  async ionViewWillEnter() {
    // skip initialization when returning from child pages (e.g., edit workouts, edit nutrition plan)
    // preserves generatedFitnessPlan so returnCallbacks (onNutritionPlanEdited etc.) can use it
    if (this.initialized) {
      return;
    }

    // reset skip flag and stale plan reference on fresh page entry only
    // generatedFitnessPlan may hold the canonical singleton reference (from skipPlanRegeneration path),
    // and if skipPlanRegeneration is reset without clearing generatedFitnessPlan,
    // replaceCurrentFitnessPlanAsync would call deepOverwriteCouchSafe(obj, obj) which destroys the singleton
    if (this.skipPlanRegeneration) {
      this.generatedFitnessPlan = undefined;
    }
    this.skipPlanRegeneration = false;
    this.nutritionResolvedForKeptPlan = false;  // #3382/#3635: reset nutrition re-solve flag on fresh entry
    this.planEditedForKeptPlan = false;  // df-919e69164e24: a previous visit's edit must not make this one write a plan
    this.fitnessStateBeforeEditWorkouts = undefined;  // df-5556257f1229: a previous visit's starting point says nothing about this one

    this.loading = true;
    this.changeDetector.markForCheck();

    await this.initializeAsync();
    if (this.viewDestroyed) return;

    this.updateRecommendations();
    // reset ChangeChecker baseline AFTER updateRecommendations() to prevent
    // initialization-induced modifications (e.g., calculateBF recalculating bf_pc,
    // calculateOverallTrainingLevel) from triggering a save when slider valueChange
    // events fire somethingChanged() during the first render pass.
    // Without this, the spurious save creates a CouchDB conflict with cross-device changes.
    this.intakeFormState_changed?.markSaved();

    this.loading = false;
    this.initialized = true;
    this.changeDetector.markForCheck();
    await waitForAngularUpdate(this.injector);
    if (this.viewDestroyed) return;
    this.changeDetector.detectChanges();

    // if in new mode, track the intake form start event
    if (this.mode === 'new') {
      this.trackIntakeStepEvent('intake_form_start');
    }
  }

  // Cache for weight preselect values to avoid recalculation
  private _weightPreselectValuesKg: number[] = null;
  private _weightPreselectValuesLb: number[] = null;

  // Generate weight preselect values - returns kg values but uses appropriate increments based on unit
  getWeightPreselectValues(unit: string): number[] {
    // Default to kg if unit is undefined or not recognized
    if (!unit || unit === Config_Kilograms.id) {
      // For kg: 5 to 300 kg in 2.5 kg increments
      if (!this._weightPreselectValuesKg) {
        this._weightPreselectValuesKg = [];
        for (let kg = 5; kg <= 300; kg += 2.5) {
          this._weightPreselectValuesKg.push(kg);
        }
      }
      return this._weightPreselectValuesKg;
    } else if (unit === Config_Pounds.id) {
      // For pounds: 10 to 650 lbs in 5 lb increments, but store as kg
      if (!this._weightPreselectValuesLb) {
        this._weightPreselectValuesLb = [];
        for (let lb = 10; lb <= 650; lb += 5) {
          this._weightPreselectValuesLb.push(lb * MassUnit.lb / MassUnit.kg); // Convert lb to kg
        }
      }
      return this._weightPreselectValuesLb;
    }
    // Fallback to kg
    return this.getWeightPreselectValues(Config_Kilograms.id);
  }

  // Generate reps preselect values
  get repsPreselectValues(): number[] {
    const values = [];
    for (let reps = 1; reps <= 20; reps++) {
      values.push(reps);
    }
    return values;
  }


  async ionViewWillLeave() {
    this.debug.setPageDebug(null);
    if (this.isCoachMode) {
      this.pubSub.publish(coachBannerEvent, null);
    }
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
    // in coach mode, skip clearing intake form state (data is on the client slot, not user's DB)
    if (this.isCoachMode) return;
    // #4139: a coached review (a coach impersonating a pre-finalize AwaitingCoachFinalize client)
    // runs in mode='change', but the client's submitted answers live ONLY in intakeFormState until
    // the coach finalizes — the declared/immutable/fitness singletons are still empty. Clearing here
    // on a non-finalize exit (back button, cancel-from-tabs, navigating elsewhere in the client's
    // account) would wipe the client's entire submission with no way to rebuild it. So skip the
    // change-mode clear for a not-yet-finalized coached review. The post-finalize clear still runs
    // via queueClearingIntakeFormState (set during finalize), by which point the answers are safely
    // persisted to the client's singletons.
    if ((this.mode === 'change' && !this.isCoachedReview()) || this.queueClearingIntakeFormState) {
      void this.clearIntakeFormAsync();
    }
  }

  // the goal to recommend switching to, or undefined when the saved goal already matches (#6291)
  protected recommendedGoalSwitch() {
    return getRecommendedGoalSwitch(
      this.intakeFormState.bfData?.bf_pc || this.lastFitnessStateBf_pc,
      this.intakeFormState.gender,
      this.lastTrainingGoalGrowth,
    );
  }

  bodyfatSkipped() {
    this.intakeFormState.bfData.method = null;
    this.intakeFormState.bfData.bf_pc = undefined;
    this.somethingChanged();
  }

  trainingFrequencyChanged() {
    // fix strings into numbers!
    if (typeof this.intakeFormState.trainingFrequency === 'string') {
      this.intakeFormState.trainingFrequency = parseInt(this.intakeFormState.trainingFrequency);
    }
    if (this.intakeFormState.trainingFrequency != this.intakeFormState.lastTrainingFrequency) {
      this.intakeFormState.lastTrainingFrequency = this.intakeFormState.trainingFrequency;

      let freq = this.intakeFormState.trainingFrequency;
      let days = [
        /* 0 */[0, 0, 0, 0, 0, 0, 0] /* shouldn't be used, but just in case */,
        /* 1 */[0, 0, 1, 0, 0, 0, 0],
        /* 2 */[1, 0, 0, 1, 0, 0, 0],
        /* 3 */[1, 0, 1, 0, 1, 0, 0],
        /* 4 */[1, 0, 1, 0, 1, 1, 0],
        /* 5 */[1, 1, 1, 0, 1, 1, 0],
        /* 6 */[1, 1, 1, 1, 1, 1, 0],
        /* 7 */[1, 1, 1, 1, 1, 1, 1],
      ][freq];

      if (days) {
        this.intakeFormState.trainingDays = days.map(d => d === 1);
      }
    }

    if (this.somethingChanged()) {
      this.trackIntakeStepEvent('intake_form_schedule_training_frequency', { value: this.intakeFormState.trainingFrequency })
    }
  }

  morningAppetiteChanged() {
    if (this.somethingChanged()) {
      this.trackIntakeStepEvent('intake_form_schedule_morning_appetite', { value: this.intakeFormState.morningAppetite ? 'yes' : 'no' });
    }
  }

  advancedTrainingMethodsMyoRepsChanged() {
    if (this.somethingChanged()) {
      let advancedMethodsText = '';
      switch (this.intakeFormState.advancedMethodsMyoReps) {
        case AdvancedMethodsMyoReps.Avoid:
          advancedMethodsText = 'avoid';
          break;
        case AdvancedMethodsMyoReps.Automatic:
          advancedMethodsText = 'automatic';
          break;
        case AdvancedMethodsMyoReps.Prefer:
          advancedMethodsText = 'prefer';
          break;
        default:
      }
      this.trackIntakeStepEvent('intake_form_advanced_training_myo_reps', { value: advancedMethodsText });
    }
  }

  advancedTrainingMethodsRMTestingChanged() {
    if (this.somethingChanged()) {
      let advancedMethodsText = '';
      switch (this.intakeFormState.advancedMethodsRMTesting) {
        case AdvancedMethodsRMTesting.Avoid:
          advancedMethodsText = 'avoid';
          break;
        case AdvancedMethodsRMTesting.Automatic:
          advancedMethodsText = 'automatic';
          break;
        case AdvancedMethodsRMTesting.Prefer:
          advancedMethodsText = 'prefer';
          break;
        default:
      }
      this.trackIntakeStepEvent('intake_form_advanced_training_rm_testing', { value: advancedMethodsText });
    }
  }

  advancedMethodsChanged() {
    this.somethingChanged();
    this.changeDetector.markForCheck();
  }

  nutritionOptionsChanged() {
    this.nutritionOptionsDescription = getNutritionOptionsDescription({
      nutritionCoaching: this.intakeFormState.nutritionCoaching !== NutritionCoachingType.None,
      nutritionTracking: this.intakeFormState.nutritionTracking !== NutritionTrackingType.None,
      macroTargetsFlexible: this.intakeFormState.macroTargets === MacroTargetsType.Flexible,
    });
    this.somethingChanged();
    this.changeDetector.markForCheck();
  }

  trainingDayChanged(value: boolean, dayIndexIndex: number) {
    this.intakeFormState.trainingDays[dayIndexIndex] = value;
    this.intakeFormState.trainingFrequency = this.intakeFormState.trainingDays.filter(d => d).length;
    if (this.somethingChanged()) {
      this.trackIntakeStepEvent('intake_form_schedule_training_frequency', { value: this.intakeFormState.trainingFrequency })
    }
  }

  onTrainingDaysChange(selectedDays: boolean[]) {
    this.intakeFormState.trainingDays = selectedDays;
    this.intakeFormState.trainingFrequency = selectedDays.filter(d => d).length;
    if (this.somethingChanged()) {
      this.trackIntakeStepEvent('intake_form_schedule_training_frequency', { value: this.intakeFormState.trainingFrequency })
    }
  }

  mealFrequencyChanged() {
    if (this.somethingChanged()) {
      this.trackIntakeStepEvent('intake_form_schedule_meal_frequency', { value: this.intakeFormState.mealFrequency })
    }
  }

  sleepHoursChanged() {
    const sleepHours = this.intakeFormState.sleepHours || 6;

    // calculate recommended wake/sleep times
    // try to get ~20% sleep before midnight, with bedtime never after 23:00
    const hoursBeforeMidnight = Math.min(sleepHours * 0.2, 1.5); // max 1.5 hours before midnight
    let bedHour = 24 - hoursBeforeMidnight; // e.g., 22.5 for 1.5 hours
    bedHour = roundToPrecision(bedHour, 0.25); // round to 15 minutes

    // if bedtime would be after 23:00, cap it at 23:00
    if (bedHour > 23) {
      bedHour = 23;
    }

    // calculate wake time
    let wakeHour = bedHour + sleepHours;
    if (wakeHour >= 24) {
      wakeHour -= 24;
    }

    // convert to time strings (HH:MM format)
    const bedHourInt = Math.floor(bedHour);
    const bedMinutes = Math.round((bedHour - bedHourInt) * 60);
    const wakeHourInt = Math.floor(wakeHour);
    const wakeMinutes = Math.round((wakeHour - wakeHourInt) * 60);

    this.intakeFormState.bedTime = `${bedHourInt.toString().padStart(2, '0')}:${bedMinutes.toString().padStart(2, '0')}`;
    this.intakeFormState.wakeupTime = `${wakeHourInt.toString().padStart(2, '0')}:${wakeMinutes.toString().padStart(2, '0')}`;

    if (this.somethingChanged()) {
      this.trackIntakeStepEvent('intake_form_schedule_sleep_hours', { value: sleepHours })
    }
  }

  // getter for template type safety - returns TrainingGoal (not boolean)
  get trainingGoalForNutritionCard(): TrainingGoal {
    const goal = this.intakeFormState.trainingGoalGrowth;
    if (typeof goal === 'boolean' || goal === undefined) {
      return TrainingGoal.Automatic;
    }
    return goal;
  }

  trainingGoalChanged() {
    if (this.somethingChanged()) {
      let trainingGoalText = '';
      switch (this.intakeFormState.trainingGoalGrowth) {
        case TrainingGoal.MuscleGrowth:
          trainingGoalText = 'muscle_growth';
          break;
        case TrainingGoal.FatLoss:
          trainingGoalText = 'fat_loss';
          break;
        case TrainingGoal.Automatic:
          trainingGoalText = 'automatic';
          break;
        default:
      }
      this.trackIntakeStepEvent('intake_form_goals_training_goal', { value: trainingGoalText });
    }
  }

  DedicationLevel = DedicationLevel;

  goalPhysiqueChanged() {
    if (this.somethingChanged()) {
      let devFocusText = '';
      switch (this.intakeFormState.muscleGrowthPreferenceMain) {
        case MuscleGrowthMain.Balanced:
          devFocusText = 'balanced';
          break;
        case MuscleGrowthMain.BareMinimum:
          devFocusText = 'bare_minimum';
          break;
        case MuscleGrowthMain.LowerBody:
          devFocusText = 'bikini_model';
          break;
        case MuscleGrowthMain.Custom:
          devFocusText = 'custom';
          break;
        default:
      }
      this.trackIntakeStepEvent('intake_form_goals_physique_goal', { value: devFocusText });
    }

    this.changeDetector.markForCheck();
  }

  nutritionToggleChanged() {
    this.intakeFormState.nutritionCoaching = this.nutritionCoachingOn ? NutritionCoachingType.Default : NutritionCoachingType.None;
    this.intakeFormState.nutritionTracking = this.nutritionTrackingOn ? NutritionTrackingType.Default : NutritionTrackingType.None;
    this.intakeFormState.macroTargets = this.macroTargetsFlexible ? MacroTargetsType.Flexible : MacroTargetsType.Exact;

    this.nutritionOptionsDescription = getNutritionOptionsDescription({
      nutritionCoaching: this.nutritionCoachingOn,
      nutritionTracking: this.nutritionTrackingOn,
      macroTargetsFlexible: this.macroTargetsFlexible,
    });
    this.trackIntakeStepEvent('intake_form_nutrition_options', {
      value: `coaching:${this.intakeFormState.nutritionCoaching};tracking:${this.intakeFormState.nutritionTracking}`
    });

    this.changeDetector.markForCheck();
  }

  somethingChanged(): boolean {
    if (this.loading) {
      return false;
    }

    this.updateRecommendations();

    // if this was triggered by a remote update (fast lane / DocWatcher), update the
    // ChangeChecker baseline AFTER updateRecommendations() so we don't re-save
    // remotely-received data (which would create needless CouchDB rev conflicts)
    if (this.remoteUpdatePending) {
      this.remoteUpdatePending = false;
      this.intakeFormState_changed?.markSaved();
      return false;
    }

    let transaction = DatabaseWrapper.getTransactionId();

    if (this.intakeFormState_changed && this.intakeFormState_changed.hasChanged()) {
      // outside new mode this is the same comparison needsPlanRegeneration() makes
      // (df-b9d12c28a387): an answer that does not ask for a Recalculate (meal frequency,
      // sleep hours) must not throw the program away either, or a rebuild throws away the
      // program it has just built. new mode keeps its own stricter rule; see SnapshotComparison
      const comparison = this.isNewMode() ? SnapshotComparison.AllAnswers : SnapshotComparison.PlanAffectingAnswers;
      if (this.intakeFormState.maxEffortPlanIntakeFormState
        && !maxEffortSnapshotMatchesForm(this.intakeFormState.maxEffortPlanIntakeFormState, this.intakeFormState, comparison)) {
        // Only clear the final generated plan, not the maxEffortPlan reference
        // maxEffortPlan will be regenerated in initializeAsync only if needed
        this.generatedFitnessPlan = undefined;
        this.intakeFormState.generatedFitnessPlan = undefined;

        // Don't clear maxEffortPlan here - it's a reference that should persist
        // It will be regenerated when needed based on needsPlanRegeneration()

        // in new mode, clear maxEffortPlan so it gets regenerated on the Goals step;
        // cap step at Goals (plan needs regeneration there) but keep a lower step
        // if the user navigated back (intakeFormProgress tracks vld-progress position)
        if (this.isNewMode()) {
          this.intakeFormState.maxEffortPlan = undefined;
          this.intakeFormState.maxEffortPlanIntakeFormState = undefined;
          const currentStep = Math.min(this.intakeFormState.intakeFormProgress, IntakeStep.Goals);
          this.intakeFormState.intakeFormProgress = currentStep;
          this.localIntakeStep = currentStep;
        }
      }
      this.intakeFormState_changed.markSaved();
      void this.userDb.saveIntakeFromStateAsync(transaction);
      this.changeDetector.markForCheck();
      return true;
    }
    return false;
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(e) {
    void e;
    //    this.somethingChanged();
  }

  @HostListener('window:click', ['$event'])
  onClick(e) {
    void e;
    //    this.somethingChanged();
  }

  shouldOfferAutomaticTrainingGoal() {
    return this.mode === 'new';
  }

  shouldCheckForBodyWeight() {
    return this.mode === 'new' || this.mode === 'change';
  }

  shouldCheckForDeclaredStrength() {
    return this.mode === 'new';
  }

  shouldOfferTapeMeasureForBF() {
    return this.mode === 'new' || this.mode === 'change';
  }

  async initializeAsync() {

    await this.exerciseDb.initializeAsync();

    // in coach mode, load data from the client slot instead of the user's DB
    if (this.isCoachMode) {
      await this.initializeCoachModeAsync();
      this.fitnessPlan = {} as FitnessPlan;
    } else {
      [this.fitnessPlan, this.intakeFormState, this.fitnessState] = await Promise.all([this.userDb.loadPlanAsync(this.docWatcher), this.userDb.loadIntakeFromStateAsync(this.docWatcher), this.userDb.loadFitnessStateAsync(this.docWatcher)]);
    }

    // option A (existing): a not-ready plan forces the linear new-mode
    // wizard. option B coach review (#3389): the client's plan is
    // intentionally not generated yet at review time, so this forcing
    // would prevent the coach from seeing the tabbed change-mode wizard.
    // Skip the forcing for coach-managing review so the coach can jump
    // between tabs.
    if (!this.fitnessPlan.ready && !this.isCoachedReview()) {
      this.mode = 'new';
    }
    // option B coach review (#3389): default to change-mode (tabbed)
    // when the coach enters via the intake-review deep link. The
    // navParam may carry mode='change' from the registered deep-link,
    // but in case the entry is via setCoachManagingClient alone, this
    // gives the same outcome.
    if (this.isCoachedReview()) {
      this.mode = 'change';
    }

    if (this.intakeFormState.time_utc === undefined) {
      this.intakeFormState.mode = this.mode;
      this.intakeFormState.time_utc = this.debug.getNowLocal().toUTC().toISO();
      // in coach mode, immutable state (born/gender/height) starts empty —
      // the coach fills them in step 1; in normal mode, pre-fill from user's DB
      if (!this.isCoachMode) {
        const immutableState = await this.userDb.loadImmutableStateAsync(this.docWatcher);
        this.intakeFormState.born = immutableState.born;
        this.intakeFormState.gender = immutableState.gender;
        this.intakeFormState.height_cm = immutableState.height_cm;
        this.intakeFormState.height_unit = immutableState.height_unit;
      }
      if (this.mode !== 'new' && !this.isCoachMode) {
        // non-coach 'change' mode: pre-fill intake form from the existing
        // user's declaredState. coach-mode 'change' is a re-edit of an
        // already-populated slot intakeFormState; loading the coach's
        // declaredState here would bleed coach data into the slot (#3289).
        //
        // this pre-fill is not the only one. the second one below re-applies every
        // field this call writes, and it runs on every path this one does:
        // assigning time_utc a few lines above satisfies its time_utc check, and
        // areAnswersOnlyInIntakeForm() is false here because this branch already
        // excludes coach mode and a coached review always has time_utc set. so the
        // only part of this branch that is uniquely its own is the
        // declaredState.trainingDays repair just below. the reason to keep this
        // call anyway is the reload path, where the form is opened with time_utc
        // already set: there this branch is skipped and only the second one runs,
        // so a field must be in the shared helper to be pre-filled at all.
        let [declaredState, schedule] = await Promise.all([this.userDb.loadDeclaredStateAsync(this.docWatcher), this.userDb.loadScheduleAsync()]);
        applySavedStateToIntakeForm(this.intakeFormState, {
          declaredState,
          fitnessState: this.fitnessState,
          schedule,
          todayISO: this.debug.getNowLocal().toISODate(),
          bodyweightUnit: this.settings.getUnitPref('bodyweight_kg'),
        });
        // If schedule training days are different than declared state fix declared state.
        if (declaredState.trainingDays.filter(e => e === true).length !== this.intakeFormState.trainingDays.filter(e => e === true).length) {
          declaredState.trainingDays = this.intakeFormState.trainingDays;
          void this.userDb.saveDeclaredStateAsync(DatabaseWrapper.getTransactionId());
        }

      } else if (this.mode === 'new') {
        this.intakeFormState.bedTime = '22:00';
        this.intakeFormState.wakeupTime = '07:00';
        this.intakeFormState.nutritionCoaching = NutritionCoachingType.Default;
        this.intakeFormState.nutritionTracking = NutritionTrackingType.Default;
        this.intakeFormState.macroTargets = MacroTargetsType.Flexible;
        if (!this.intakeFormState.trainingDays) {
          this.intakeFormState.trainingDays = [false, false, false, false, false, false, false];
          this.intakeFormState.trainingFrequency = 0;
        }
      }
    }

    // recover generatedFitnessPlan from intakeFormState if available (e.g., after PWA reload)
    if (!this.generatedFitnessPlan && this.intakeFormState.generatedFitnessPlan) {
      this.installGeneratedPlan(this.intakeFormState.generatedFitnessPlan);
      await Promise.all([this.refreshWorkoutsList(), this.refreshNutritionDataAsync()]);
    }

    if (this.mode !== 'new') {
      this.lastBFMethod = this.fitnessState.bfData && this.fitnessState.bfData.method;
      this.lastFitnessStateBf_pc = this.fitnessState.bfData.bf_pc;
      this.lastTrainingGoalGrowth = this.intakeFormState.trainingGoalGrowth;
      this.currentIntakeDeltaPercent = this.fitnessPlan.intakeDeltaPercent;
      this.currentTrainingVolumeWeeklyAvg = this.fitnessPlan.trainingVolumeWeeklyAvg;

      // if intakeFormState was already initialized (time_utc is set), reload ALL solver-critical
      // fields from declaredState — a stale intakeFormState from an incomplete previous session
      // may be missing fields that fillDeclaredState() would overwrite with undefined, crashing
      // the solver (e.g. trainingDays, declaredStrength, trainingGoalGrowth).
      // Skipped when the answers live only in the intake form (see
      // areAnswersOnlyInIntakeForm): there is no declaredState here worth reloading from, and
      // reloading would either bleed coach data into the slot or wipe the client's submission.
      if (this.intakeFormState.time_utc !== undefined && !this.areAnswersOnlyInIntakeForm()) {
        const [declaredState, schedule] = await Promise.all([this.userDb.loadDeclaredStateAsync(this.docWatcher), this.userDb.loadScheduleAsync()]);
        applySavedStateToIntakeForm(this.intakeFormState, {
          declaredState,
          fitnessState: this.fitnessState,
          schedule,
          todayISO: this.debug.getNowLocal().toISODate(),
          bodyweightUnit: this.settings.getUnitPref('bodyweight_kg'),
        });
        // if bfData is missing (stale session never reached body composition step),
        // initialize from fitnessState so fillFitnessState() doesn't crash on undefined
        if (!this.intakeFormState.bfData) {
          this.intakeFormState.bfData = { ...this.fitnessState.bfData };
        }
        this.changeDetector.markForCheck();
      }

      // only regenerate maxEffortPlan if it doesn't exist or relevant data has changed
      // this avoids unnecessary server calls and improves performance
      if (!this.intakeFormState.maxEffortPlan || this.needsPlanRegeneration()) {
        // save the user's chosen values before generateProgramAsync deletes them
        const savedTrainingVolume = this.intakeFormState.trainingVolumeWeeklyAvg;
        const savedIntakeDelta = this.intakeFormState.intakeDeltaPercent;

        await this.generateProgramAsync(true);

        // restore the user's chosen values (generateProgramAsync sets them to suggested values)
        if (savedTrainingVolume !== undefined) {
          this.intakeFormState.trainingVolumeWeeklyAvg = savedTrainingVolume;
        }
        if (savedIntakeDelta !== undefined) {
          this.restoreSavedIntakeDeltaIfCompatible(savedIntakeDelta);
        }

        // update the snapshot to include the restored values
        // this ensures the snapshot matches the current state
        // ignoreMap drops the snapshot key itself so it never nests into its own snapshot (#3405)
        this.intakeFormState.maxEffortPlanIntakeFormState = deepClone(this.intakeFormState, { ignoreMap: { maxEffortPlanIntakeFormState: true } });
      }

      // trigger change detection after initialization
      this.changeDetector.markForCheck();

      // ensure tab selection is properly applied after initialization
      // on page reload, the tab from URL params might not be properly selected
      // force a re-selection to ensure the tab component updates
      const currentTab = this._selectedTab;

      if (currentTab !== IntakeStep.BodyComposition) {
        // temporarily set to a different value to force change detection
        this._selectedTab = IntakeStep.BodyComposition;
        this.changeDetector.detectChanges();
        await waitForAngularUpdate(this.injector);

        // now set it back to the intended tab. The wait above now returns when this
        // page is destroyed instead of hanging (df-3f0eaff27ac6), so only the redraw
        // is skipped in that case — the rest of this method still sets up the form
        // model, and returning early here would silently drop all of it
        this._selectedTab = currentTab;
        if (!this.viewDestroyed) this.changeDetector.detectChanges();
      } else {
        // if already on BodyComposition, just trigger change detection
        this.changeDetector.detectChanges();
      }
    } else {
      if (this.intakeFormState.bfData == null || !this.intakeFormState.bfData.method) {
        if (!this.intakeFormState.bfData) this.intakeFormState.bfData = {} as any;
        this.intakeFormState.bfData.method = BFMethod.Self;
        if (this.intakeFormState.bfData.method === BFMethod.Self) {
          if (this.intakeFormState.bfData.bfSelf_pc === undefined) {
            this.intakeFormState.bfData.bfSelf_pc = 20.0;
          }
        }
      }
    }

    if (this.intakeFormState.advancedMethodsMyoReps === undefined) {
      this.intakeFormState.advancedMethodsMyoReps = AdvancedMethodsMyoReps.Automatic;
    }
    if (this.intakeFormState.advancedMethodsRMTesting === undefined) {
      this.intakeFormState.advancedMethodsRMTesting = AdvancedMethodsRMTesting.Prefer;
    }
    if (this.intakeFormState.advancedMethodsCalorieCycling === undefined) {
      this.intakeFormState.advancedMethodsCalorieCycling = false;
    }

    if (this.mode === 'change') {
      this.intakeFormState.intakeFormProgress = 1;
    }

    // ensure intakeFormProgress is initialized
    if (!this.intakeFormState.intakeFormProgress) {
      this.intakeFormState.intakeFormProgress = 1;
    }

    // ProgramOverview and the first-workout handoff (#4059) are transient end-of-intake
    // steps that should not persist on refresh unless we have a generated plan to show
    // (e.g., reload during intake). Both fall back to EnergyBalance when the plan is gone.
    if (this.intakeFormState.intakeFormProgress >= IntakeStep.ProgramOverview && !this.generatedFitnessPlan) {
      this.intakeFormState.intakeFormProgress = IntakeStep.EnergyBalance;
    }

    // #4059: if we resumed directly onto the first-workout handoff (e.g. a PWA reload),
    // recompute Start-button eligibility — it is otherwise only set on the 11->12 tap, so
    // without this the reloaded user would lose the "Start your first workout" CTA.
    if (this.intakeFormState.intakeFormProgress === IntakeStep.FirstWorkoutHandoff) {
      await this.computeFirstWorkoutStartabilityAsync();
    }

    // initialize button ID based on current step
    this.nextButtonId = `nextButtonStep${this.intakeFormState.intakeFormProgress}`;

    if (!this.intakeFormState.trainingDays) {
      this.intakeFormState.trainingDays = [];
    }
    for (let i = 0; i < 7; i++) {
      if (this.intakeFormState.trainingDays[i] == null) {
        this.intakeFormState.trainingDays[i] = false;
      }
    }
    this.intakeFormState.trainingGoalGrowth =
      this.intakeFormState.trainingGoalGrowth !== undefined ? this.intakeFormState.trainingGoalGrowth : TrainingGoal.Automatic;
    this.intakeFormState.muscleGrowthPreferenceMain =
      this.intakeFormState.muscleGrowthPreferenceMain !== undefined
        ? this.intakeFormState.muscleGrowthPreferenceMain
        : MuscleGrowthMain.Balanced;
    this.intakeFormState.muscleGrowthPreferenceTable = this.intakeFormState.muscleGrowthPreferenceTable || {
      ['glutes']: OneMuscleGrowthValue.Maximize,
      ['glutemed']: OneMuscleGrowthValue.Maintain,
      ['quads']: OneMuscleGrowthValue.Maximize,
      ['hams']: OneMuscleGrowthValue.Maximize,
      ['calves']: OneMuscleGrowthValue.Maximize,
      ['shoulders']: OneMuscleGrowthValue.Maximize,
      ['chest']: OneMuscleGrowthValue.Maximize,
      ['lats']: OneMuscleGrowthValue.Maximize,
      ['traps']: OneMuscleGrowthValue.Maximize,
      ['triceps']: OneMuscleGrowthValue.Maximize,
      ['biceps']: OneMuscleGrowthValue.Maximize,
      ['abs']: OneMuscleGrowthValue.Maintain,
    };
    // migration: add glutemed for existing intake form data
    if (this.intakeFormState.muscleGrowthPreferenceTable['glutemed'] === undefined) {
      this.intakeFormState.muscleGrowthPreferenceTable['glutemed'] = OneMuscleGrowthValue.Maintain;
    }

    // this will initialize the declaredStrength if it is not initialized yet, or consoliudate it with the existing data
    await this.loadTestingExercisesAsync();

    this.intakeFormState_changed = new ChangeChecker(this.intakeFormState);
    this.localIntakeStep = this.intakeFormState.intakeFormProgress;

    this.additionalImages = [];
    this.frontImage = null;
    this.backImage = null;

    this.nutritionCoachingOn = this.intakeFormState.nutritionCoaching === NutritionCoachingType.Default;
    this.nutritionTrackingOn = this.intakeFormState.nutritionTracking === NutritionTrackingType.Default;
    this.macroTargetsFlexible = this.intakeFormState.macroTargets === MacroTargetsType.Flexible;
    this.nutritionOptionsDescription = getNutritionOptionsDescription({ nutritionCoaching: this.nutritionCoachingOn, nutritionTracking: this.nutritionTrackingOn, macroTargetsFlexible: this.macroTargetsFlexible });

    this.debug.setPageDebug({
      intakeFormState: this.intakeFormState,
    });

    if (this.generatedFitnessPlan) {
      void this.refreshWorkoutsList();
    }

    this.updateDedicationSliders();

    this.changeDetector.markForCheck();
  }

  updateFrontImageHandler(image: ScaledImage) {
    this.frontImage = image;
    this.somethingChanged();
  }

  updateBackImageHandler(image: ScaledImage) {
    this.backImage = image;
    this.somethingChanged();
  }

  updateExistingImageHandler(image: ScaledImage, iImage: number) {
    if (image === null) {
      this.additionalImages.splice(iImage, 1);
      return;
    }
    this.additionalImages[iImage] = image;
    this.somethingChanged();
  }

  uploadNewImageHandler(image: ScaledImage) {
    this.additionalImages.push(image);
  }

  handleTimeChanged(timeISO: string, propName: string) {
    this.intakeFormState[propName] = timeISO;
    this.somethingChanged();
  }

  async loadTestingExercisesAsync() {
    this.strengthTestingSlots = [];
    this.strengthTestingExercises = [];

    let slots: ExerciseSlot[];
    try {
      slots = await this.exerciseDb.getAllTestingSlotsAsync(this.docWatcher);
    } catch (err) {
      // a database that is shutting down is an expected state: let it travel, as before
      if (isDatabaseTornDown(err)) {
        throw err;
      }
      // the slot list could not be read at all, so there is nothing to check the user's
      // saved answers against: leave them as they are and say the step cannot load (#6540)
      this.logging.trackSilentProblem('StrengthTestSlotsReadFailed', { error: describeErrorForLog(err) });
      this.strengthTestsLoad = StrengthTestsLoad.ReadFailed;
      return;
    }
    if (slots.length === 0) {
      this.logging.trackSilentProblem('No strength testing slots found', {});
    }
    const loaded = await prepareStrengthStepAsync(slots, this.intakeFormState.declaredStrength, this.exerciseDb, this.logging, this.docWatcher);
    this.strengthTestsLoad = loaded.status;
    this.strengthTestingSlots = loaded.slots;
    this.strengthTestingExercises = loaded.exercises;
  }

  /** the Strength card's Skip, shown while the strength tests could not be loaded */
  strengthTestsSkipped() {
    this.trackIntakeStepEvent('intake_form_strength_tests_skipped');
  }

  /** the Strength card's Try Again: read the strength tests once more */
  async retryStrengthTestsAsync() {
    await this.loadTestingExercisesAsync();
    if (this.viewDestroyed) return;
    this.changeDetector.markForCheck();
  }

  getTestingExerciseById(id: string): Exercise {
    return this.strengthTestingExercises.find(e => e._id === id);
  }

  nextExerciseIdInSlot(slot: DeclaredStrength_Slot): string {
    let uiSlot = this.strengthTestingSlots.find(s => s.idSlot === slot.idSlot);
    let index = uiSlot.exercises.findIndex(e => e.exercise._id === slot.idExercise);
    if (index >= 0 && index < uiSlot.exercises.length - 1) {
      return uiSlot.exercises[index + 1].exercise._id;
    }
    return null;
  }

  getTestingExerciseName(id: string) {
    let e = this.getTestingExerciseById(id);
    return e ? e.name : '?';
  }

  isTestingExerciseBW(id: string) {
    let e = this.getTestingExerciseById(id);
    return e && e.bw_pc > 0;
  }

  // slot names come from the exercise catalog (admin-authored content), not the LL catalog;
  // the 'unknown' fallback is a pre-existing dev-facing placeholder for a lookup miss, also not catalog text
  getSlotName(id: string): TranslatedString {
    let uiSlot = this.strengthTestingSlots.find(s => s.idSlot === id);
    if (uiSlot) {
      return untranslatedString(uiSlot.slotName);
    }
    return untranslatedString('unknown');
  }

  getExercisesForSlot(idSlot: string): StrengthTestingUI_Exercise[] {
    let uiSlot = this.strengthTestingSlots.find(s => s.idSlot === idSlot);
    return uiSlot ? uiSlot.exercises : [];
  }

  getWeightUnitLabel(unit: string): TranslatedString {
    // a measurement-unit abbreviation ('Lb' / 'Kg'), not translatable text
    return unit === 'lb' ? untranslatedString('Lb') : untranslatedString('Kg');
  }

  onExerciseChanged(_slot: DeclaredStrength_Slot) {
    // called when user changes exercise in dropdown
    this.somethingChanged();
    this.changeDetector.markForCheck();
  }

  onStrengthInputChanged() {
    // called when user enters weight or reps
    // auto-update hasPreviousPerformance based on whether any inputs are filled
    this.updateHasPreviousPerformance();
    this.somethingChanged();
  }

  updateHasPreviousPerformance() {
    // check if any exercise has weight or reps entered
    const hasAnyInput = this.intakeFormState.declaredStrength.slots.some(slot => {
      return (slot.stats.weight_kg != null && slot.stats.weight_kg > 0) ||
             (slot.stats.reps != null && slot.stats.reps > 0);
    });

    this.intakeFormState.declaredStrength.hasPreviousPerformance = hasAnyInput ? true : false;
  }

  changeExercise(slot: DeclaredStrength_Slot) {
    let skip = (avoid: boolean) => {
      if (slot.idExercise) {
        let exerciseStats = this.intakeFormState.exerciseStats[slot.idExercise];
        if (!exerciseStats) {
          exerciseStats = this.intakeFormState.exerciseStats[slot.idExercise] = {} as ExerciseStat;
        }
        if (avoid) {
          ExerciseStat.markSwitched(exerciseStats, this.debug.getNowLocal().toUTC());
        }
      }

      // use next exercise instead
      let nextExerciseId = this.nextExerciseIdInSlot(slot);
      // this clears all other data members (e.g. pre-entered, or calculated values)
      deepOverwrite(slot, { idSlot: slot.idSlot, idExercise: nextExerciseId, stats: {} });
      this.changeDetector.markForCheck();
    };

    void this.alertCtrl.showAlertAsync({
      message: LL.intake.strength.skipExerciseReason(),
      buttons: [
        { text: LL.intake.strength.neverTrained(), role: AlertButtonRole.Cancel, result: () => skip(false) },
        {
          text: LL.intake.strength.injuryOrEquipment(),
          role: AlertButtonRole.Primary,
          result: () => skip(true),
        },
      ],
    });
  }
  revertSlot(slot: DeclaredStrength_Slot) {
    let uiSlot = this.strengthTestingSlots.find(s => s.idSlot === slot.idSlot);
    if (uiSlot && uiSlot.exercises && uiSlot.exercises[0] && uiSlot.exercises[0].exercise) {
      slot.idExercise = uiSlot.exercises[0].exercise._id;
    }
  }

  // used by [validity] property in vld-error component in the form
  validity(field: string): string {
    // early exit if data is still loading
    if (this.loading) {
      return null;
    }
    // the Strength step's "tests not loaded" check; when it applies is strengthLoadBlocksNext's job (#6540)
    if (field === 'strength/load') {
      const blocks = strengthLoadBlocksNext({
        newIntake: this.isNewMode(),
        load: this.strengthTestsLoad,
        intakeProgress: this.intakeFormState.intakeFormProgress,
      });
      return blocks ? LL.intake.validation.strengthTestsNotLoaded() : null;
    }
    if (field.startsWith('strength/')) {
      let i = parseInt(field.split('/')[1]);
      return this.validityOfStrength(i);
    }

    switch (field) {
      case 'gender':
        {
          if (this.intakeFormState.gender != Gender.male && this.intakeFormState.gender != Gender.female) {
            return LL.intake.validation.gender();
          }
        }
        break;
      case 'birthday':
        {
          if (!this.intakeFormState.born) {
            return LL.intake.validation.age();
          }

          let born = DateTime.fromISO(this.intakeFormState.born);
          let age = -born.diffNow().as('years');
          if (age < 0) {
            return LL.intake.validation.birthdayFuture();
          }

          if (age < 18) {
            return LL.intake.validation.ageMinimum();
          }

          if (age > 120) {
            return LL.intake.validation.ageTooOld();
          }
        }
        break;
      case 'height':
        {
          if (!this.intakeFormState.height_cm) {
            return LL.intake.validation.height();
          }
        }
        break;
      case 'finger':
      case 'wrist':
      case 'ankle':
        {
          if (
            (field == 'finger' && this.intakeFormState.longerFinger == null) ||
            (field == 'wrist' && this.intakeFormState.wrist_cm == null) ||
            (field == 'ankle' && this.intakeFormState.ankle_cm == null)
          ) {
            return LL.intake.validation.bodyMeasurements();
          }
        }
        break;
      case 'weight':
        {
          if (this.intakeFormState.weight_kg === null || this.intakeFormState.weight_kg === undefined) {
            return LL.intake.validation.weight();
          }
        }
        break;
      case 'bf':
        {
          if (!this.intakeFormState.bfData) return LL.intake.validation.selectOption();
          let error = calculateBF(this.intakeFormState.bfData, this.intakeFormState.gender, this.intakeFormState.born, this.intakeFormState.height_cm, bfErrorTexts());
          if (error) {
            return error;
          }
        }
        break;
      case 'activity':
        {
          if (!this.intakeFormState.activityLevel) {
            return LL.intake.validation.chooseOption();
          }
        }
        break;
      case 'sleepQuality':
        {
          if (!this.intakeFormState.sleepQuality) {
            return LL.intake.validation.chooseOption();
          }
        }
        break;
      case 'stress':
        {
          if (!this.intakeFormState.stressLevel) {
            return LL.intake.validation.chooseOption();
          }
        }
        break;
      case 'goal':
        {
          if (this.intakeFormState.trainingGoalGrowth === null || this.intakeFormState.trainingGoalGrowth === undefined) {
            return LL.intake.validation.chooseOption();
          }
          if (!this.intakeFormState.maxEffortPlan) {
            //return '';
          }
        }
        break;
      case 'frequency':
        {
          if (!this.intakeFormState.trainingDays) return LL.intake.validation.selectOption();
          for (let d of this.intakeFormState.trainingDays) {
            if (d == null) {
              return LL.intake.validation.weekdaySelection();
            }
          }
          let selectedDays = this.intakeFormState.trainingDays.filter(d => d).length;
          if (selectedDays < 1) {
            return LL.intake.validation.trainingDaysAtLeastOne();
          }
        }
        break;
      case 'morningAppetite':
        {
          if (!this.intakeFormState.morningAppetite === undefined) {
            return LL.intake.validation.chooseOption();
          }
        }
        break;
      case 'meals':
        {
          if (!this.intakeFormState.mealFrequency) {
            return LL.intake.validation.chooseOption();
          }
        }
        break;
      case 'wakeBedTime':
        {
          if (this.intakeFormState.wakeupTime == null || this.intakeFormState.bedTime == null) {
            return LL.intake.validation.bothTimes();
          }
        }
        break;
      default:
        return LL.intake.validation.unknownField();
    }
    return null;
  }

  onBodyCompositionIntakeSectionSkipButtonClick() {
    this.trackIntakeStepEvent('intake_form_body_composition_skipped');
  }

  trackIntakeStepEvent(eventName: string, extraParams?: any) {
    // if not in new mode, use program_edit_ prefix instead of intake_form_
    const actualEventName = this.mode === 'new'
      ? eventName
      : eventName.replace('intake_form_', 'program_edit_');

    let params;
    if (extraParams) {
      params = deepClone({ intake_mode: this.mode, intake_form_state: this.intakeFormState, extra: extraParams }, { skipPouch: true });
    } else {
      params = deepClone({ intake_mode: this.mode, intake_form_state: this.intakeFormState }, { skipPouch: true });
    }
    this.telemetry.trackEvent(actualEventName, params);
  }

  onBodyFatMethodChange() {
    if (this.intakeFormState.bfData.method == BFMethod.Self) {
      if (this.intakeFormState.bfData.bfSelf_pc === undefined) {
        this.intakeFormState.bfData.bfSelf_pc = 20.0;
      }
    }
    this.trackIntakeStepEvent('intake_form_body_composition_bodyfat_method', { value: this.intakeFormState.bfData.method });
    this.somethingChanged();
  }

  interpolateStrength(a: StrengthPerBW[], bw_kg: number): StrengthPerBW {
    let minS = a[0];
    let maxS = a[0];

    for (let i = 1; i < a.length; i++) {
      let s = a[i];
      if (s.bw_kg <= bw_kg && (s.bw_kg > minS.bw_kg || minS.bw_kg > bw_kg)) {
        minS = s;
      }

      if (s.bw_kg >= bw_kg && (s.bw_kg < maxS.bw_kg || maxS.bw_kg < bw_kg)) {
        maxS = s;
      }
    }

    if (!(minS.bw_kg <= maxS.bw_kg)) {
      console.log(minS.bw_kg, maxS.bw_kg, bw_kg);
    }

    //console.assert(minS.bw_kg <= maxS.bw_kg);
    if (!isFinite(bw_kg) || bw_kg <= minS.bw_kg) {
      return minS;
    }

    if (bw_kg >= maxS.bw_kg) {
      return maxS;
    }

    let midS = new StrengthPerBW();
    midS.bw_kg = bw_kg;
    midS.classes = [];

    let factor = (midS.bw_kg - minS.bw_kg) / (maxS.bw_kg - minS.bw_kg);
    let lerp = (a: number, b: number) => a * (1 - factor) + b * factor;

    let n = Math.min(minS.classes.length, maxS.classes.length); // paranoia (they should be same), but just in case
    for (let i = 0; i < n; i++) {
      midS.classes[i] = {
        level: minS.classes[i].level,
        min1rm_kg: lerp(minS.classes[i].min1rm_kg, maxS.classes[i].min1rm_kg),
      };
    }

    return midS;
  }

  validityOfStrength(i: number) {
    if (!this.intakeFormState.declaredStrength?.slots?.[i]) return null;
    let s = this.intakeFormState.declaredStrength.slots[i];
    s.stats = s.stats || {}; // guard against uninitialized data

    // if user has not selected an exercise or hasn't filled in stats, treat as skipped
    if (!s.idExercise || !s.stats.weight_kg || !s.stats.reps) {
      return null;
    }

    const sts = this.strengthTestingSlots.find(s2 => s2.idSlot === s.idSlot);
    if (!sts) {
      return LL.intake.validation.internalError({ code: 'E004' });
    }

    if (s.stats.reps > 10) {
      s.trainingLevel = TrainingLevel.Novice;
      return null;
    }

    // calculate 1RM
    let e = this.getTestingExerciseById(s.idExercise);
    if (e === undefined || e === null) {
      this.logging.trackSilentProblem('Strength test exercise not found', { id: s.idExercise, stats: s.stats, intakeState: this.intakeFormState, strengthTestingExercises: this.strengthTestingExercises });
      return LL.intake.errors.internalError({ code: 'E006' });
    }
    let bw_kg = this.intakeFormState.weight_kg;
    s.stats.bw_pc = e.bw_pc; // this can be undefined. that's normal!
    estimate1RM(s.stats, bw_kg);

    // determine exact referent standard
    if (e.standard == null) {
      return LL.intake.validation.internalError({ code: 'E001' });
    }

    let refS = e.standard;
    let refW = s.stats.estimated1rm_net_kg; // note that net has to be used here! $chin_up is compared to $bench_press tables, therefore this.
    if (isStrengthStandardRelative(refS)) {
      let refE = this.getTestingExerciseById(refS.idReferenceExercise);
      if (refE == null || refE.standard == null || isStrengthStandardRelative(refE.standard)) {
        return LL.intake.validation.internalError({ code: 'E002' });
      }

      refW /= refS.factor1RM_pc; // actual performance is divided by the factor - effect is as if the standards are multiplied by the factor
      refS = refE.standard;
    }

    let a = this.intakeFormState.gender == Gender.male ? refS.men : refS.women;
    if (a.length < 1) {
      return LL.intake.validation.internalError({ code: 'E003' });
    }

    // find interpolated weight category for user's weight
    let category = this.interpolateStrength(a, bw_kg);
    // determine in which advancement level is the user
    // (note that the classes track _minimum_ weight to enter that class. if none found, user is novice.
    // we need to find the _highest_ category!)
    let found: StrengthClass = null;
    for (let c of category.classes) {
      if (refW >= c.min1rm_kg) {
        if (found === null || c.min1rm_kg > found.min1rm_kg) {
          found = c;
        }
      }
    }
    s.trainingLevel = found != null ? found.level : TrainingLevel.Novice;

    // now double check for insane values (more than 3x above advanced)
    if (refW > 3 * category.classes[category.classes.length - 1].min1rm_kg) {
      return LL.intake.validation.strengthTooGoodToBeTrue();
    }

    return null;
  }

  calculateOverallTrainingLevel() {
    let declaredStrength = this.intakeFormState.declaredStrength;

    declaredStrength.trainingLevelAverage = averageTrainingLevels(declaredStrength.slots.map(s => s.trainingLevel));
  }

  askMorningAppetite() {
    let age = -DateTime.fromISO(this.intakeFormState.born).diffNow().as('years');
    return age >= 65 || this.intakeFormState.activityLevel <= ActivityLevel.SomewhatActive;
  }

  // things we think we know better than the user...
  updateRecommendations() {
    this.recommendations.trainingFrequencyText = '';
    this.recommendations.trainingFrequencyGreat = false;
    let freq = this.intakeFormState.trainingFrequency;
    if (freq < 3) {
      this.recommendations.trainingFrequencyText =
        'If possible, you should train at least 3 days a week. Less than that is sustainable, but not ideal.';
    } else if (freq === 7) {
      this.recommendations.trainingFrequencyGreat = true;
      this.recommendations.trainingFrequencyText = "That's great! You will achieve maximum results sooner.";
    }

    this.recommendations.trainingDaysText = '';
    let actualFreq = this.intakeFormState.trainingDays.filter(d => d).length;
    if (actualFreq < 1) {
      this.recommendations.trainingDaysText = `You must select at least one training day.`;
    } else if (actualFreq > 1 && actualFreq < 7) {
      let days = this.intakeFormState.trainingDays;
      // find the first training day after a break
      let iFirstTraining = days.findIndex((d, i) => d && !days[(i + 6) % 7]) || 0;

      let breakLens = [];
      let breakLen = 0;
      let streakLens = [];
      let streakLen = 1;
      for (let i = 1; i < 8; i++) {
        let d = days[(iFirstTraining + i) % 7];
        if (d) {
          streakLen++;
          if (breakLen > 0) {
            breakLens.push(breakLen);
            breakLen = 0;
          }
        } else {
          breakLen++;
          if (streakLen > 0) {
            streakLens.push(streakLen);
            streakLen = 0;
          }
        }
      }
      streakLens = streakLens.sort();
      breakLens = breakLens.sort();

      let allowed = [
        /* 0 */ { freq: 0, streaks: [], breaks: [7] },
        /* 1 */ { freq: 1, streaks: [1], breaks: [6] },
        /* 2 */ { freq: 2, streaks: [1, 1], breaks: [2, 3] },
        /* 3 */ { freq: 3, streaks: [1, 1, 1], breaks: [1, 1, 2] },
        /* 4 */ { freq: 4, streaks: [1, 1, 2], breaks: [1, 1, 1] },
        /* 4 */ { freq: 4, streaks: [2, 2], breaks: [1, 2] },
        /* 5 */ { freq: 5, streaks: [2, 3], breaks: [1, 1] },
        /* 6 */ { freq: 6, streaks: [6], breaks: [1] },
        /* 7 */ { freq: 7, streaks: [7], breaks: [0] },
      ].find(a => a.freq === actualFreq && matches(a.streaks)(streakLens) && matches(a.breaks)(breakLens));
      //      console.log(`streakLens: ${streakLens.toString()}, breakLens: ${breakLens.toString()}, matches: ${allowed} `)

      if (!allowed) {
        this.recommendations.trainingDaysText = 'You should spread the workouts more evenly throughout the week.';
      }
    }

    if (this.mode === 'new') {
      calculateBF(this.intakeFormState.bfData, this.intakeFormState.gender, this.intakeFormState.born, this.intakeFormState.height_cm, bfErrorTexts());
      // bf_pc change will be detected by hasChanged() in the calling somethingChanged()
      this.calculateOverallTrainingLevel();
    }

    let recommendedMealFrequency: number = null;
    let age = -DateTime.fromISO(this.intakeFormState.born).diffNow().as('years');
    // setup default meal frequency
    if (
      age >= 65 ||
      (this.intakeFormState.morningAppetite === false && this.intakeFormState.activityLevel <= ActivityLevel.SomewhatActive)
    ) {
      recommendedMealFrequency = 3;
    } else {
      recommendedMealFrequency = 4;
    }

    this.recommendations.mealFrequencyText = '';
    if (recommendedMealFrequency && recommendedMealFrequency != this.intakeFormState.mealFrequency) {
      this.recommendations.mealFrequencyText = `For your current state and plan, we recommend ${recommendedMealFrequency} meals per day.`;
      if (this.intakeFormState.mealFrequency) {
        this.recommendations.mealFrequencyText += ` But you can use ${this.intakeFormState.mealFrequency} if you want.`;
      }
    }

    this.sleepDuration = null;
    if (this.intakeFormState.wakeupTime && this.intakeFormState.bedTime) {
      let wakeParsed = this.intakeFormState.wakeupTime.match(/(..):(..)/) || ['', '00', '00'];
      let bedParsed = this.intakeFormState.bedTime.match(/(..):(..)/) || ['', '00', '00'];
      let wake = parseInt(wakeParsed[1]) + parseInt(wakeParsed[2]) / 60;
      let bed = parseInt(bedParsed[1]) + parseInt(bedParsed[2]) / 60;
      this.sleepDuration = wake < bed ? 24 - bed + wake : wake - bed;
      this.sleepDuration = Math.floor(this.sleepDuration * 2) / 2;
    }

    // check that there are no recommendations
    this.recommendations.ideal = Object.keys(this.recommendations)
      .map(k => this.recommendations[k] && this.recommendations[k] !== 'sleepText')
      .filter(v => typeof v == 'string')
      .reduce((ideal, text) => ideal && !text, true);

    this.recommendations.sleepText = '';
    this.recommendations.sleepTextType = '';
    if (this.sleepDuration) {
      if (this.sleepDuration >= 8) {
        this.recommendations.sleepText = `${this.sleepDuration} hours - great.`;
        this.recommendations.sleepTextType = 'success-strong';
        this.intakeFormState.sleepQuality = SleepQuality.Great;
      } else if (this.sleepDuration < 8 && this.sleepDuration >= 7.5) {
        this.recommendations.sleepText = `${this.sleepDuration} hours - good, but could be longer.`;
        this.recommendations.sleepTextType = 'info';
        this.intakeFormState.sleepQuality = SleepQuality.Average;
        this.recommendations.ideal = false;
      } else if (this.sleepDuration < 7.5) {
        this.recommendations.sleepText = `${this.sleepDuration} hours - this is too little sleep, and will be limiting your gains.`;
        this.recommendations.sleepTextType = 'info';
        this.intakeFormState.sleepQuality = SleepQuality.Poor;
        this.recommendations.ideal = false;
      }
    }

    this.recommendations.advancedMethodsMyoRepsText = '';
    // if the user chose both timesaver and slow & steady, we warn that it might not make a difference
    if (this.intakeFormState.maxEffortPlan && this.intakeFormState.advancedMethodsMyoReps && this.intakeFormState.advancedMethodsMyoReps === AdvancedMethodsMyoReps.Prefer && this.intakeFormState.intakeDeltaPercent < this.getIntakeDeltaPercentForDedicationLevel(DedicationLevel.SlowAndSteady)) {
      this.recommendations.advancedMethodsMyoRepsText =
        "Since you selected slow and steady training volume, your program is unlikely to incorporate Myo-reps frequently. However, if you aim to minimize your gym time, you can keep the 'Time-saver' Myo-reps option for added assurance.";
    }

    // make sure updated strings show in the DOM
    this.changeDetector.markForCheck();
  }

  private markVldGroupsDone(upToStep: number) {
    if (!this.vldProgress?.groups) return;
    this.vldProgress.maxStep = upToStep;
    this.vldProgress.groups.forEach(g => {
      if (g.stepNumber < upToStep) {
        g.state = VldGroupState.Done;
      }
    });
  }

  handleBackButton() {
    if (this.intakeFormState.intakeFormProgress > 1) {
      this.intakeFormState.intakeFormProgress--;
      this.nextButtonId = `nextButtonStep${this.intakeFormState.intakeFormProgress}`;
    }
  }

  handleSettingsClick() {
    void this.navCtrl.setRootAsync(ISettingsPage, {});
  }

  async handleNextButton() {
    if (!this.vldProgress) {
      return;
    }

    // find the current step's vld-group
    const currentGroup = this.vldProgress.groups.find(
      g => g.stepNumber === this.intakeFormState.intakeFormProgress
    );

    if (currentGroup) {
      // update button ID to trigger change detection immediately
      // this ensures E2E tests can detect the button disappearing
      this.nextButtonId = `nextButtonStep${this.intakeFormState.intakeFormProgress + 1}`;
      this.changeDetector.detectChanges();

      // capture step before generateProgramAsync, which pre-advances intakeFormProgress
      const stepBeforeGenerate = this.intakeFormState.intakeFormProgress;

      // if the strength tests could not be loaded, try once more before the step's
      // validation runs, so a user who is back online passes without tapping Try Again
      if (stepBeforeGenerate === IntakeStep.Strength && this.strengthTestsLoad === StrengthTestsLoad.ReadFailed) {
        await this.retryStrengthTestsAsync();
        if (this.viewDestroyed) return;
        // render now: the step's validation below reads each message's validity as last
        // rendered, so without this a retry that succeeded would still refuse this tap
        this.changeDetector.detectChanges();
      }

      // handle special actions BEFORE progressing to next step
      if (this.intakeFormState.intakeFormProgress === IntakeStep.Goals) {
        // option B coach onboarding (#3389): client has finished the
        // intake wizard's client-side portion (steps 1-8). Instead of
        // generating the max-effort plan and moving to TrainingVolume,
        // submit the answers and hold the client on the awaiting-coach
        // page until the coach finalizes the plan.
        if (this.isCoachedOnboarding()) {
          await this.submitCoachedIntakeAndHoldAsync();
          return;
        }
        // option A (existing flow): generate the initial program and move
        // to TrainingVolume.
        if (!await this.generateProgramAsync(true)) return;
      }
      // if leaving Energy Balance step (step 10), generate the final program
      else if (this.intakeFormState.intakeFormProgress === IntakeStep.EnergyBalance) {
        if (!await this.generateProgramAsync()) return;
      }
      // #4059: leaving Program Overview (step 11) → entering the first-workout
      // handoff (step 12). new end users only — coaches finish on step 11 (their
      // footer shows Finish, not Next). decide whether "Start your first workout"
      // can launch a session today (a workout scheduled within the next week).
      else if (this.intakeFormState.intakeFormProgress === IntakeStep.ProgramOverview) {
        await this.computeFirstWorkoutStartabilityAsync();
        this.trackIntakeStepEvent('intake_form_first_workout_handoff_shown', { startable: this.hasStartableFirstWorkout });
      }

      // track step completion event using step names
      const stepEventMap: Partial<Record<IntakeStep, string>> = {
        [IntakeStep.BasicInfo]: 'intake_form_basic_info_next',
        [IntakeStep.BodyComposition]: 'intake_form_body_composition_next',
        [IntakeStep.Strength]: 'intake_form_strength_next',
        [IntakeStep.Lifestyle]: 'intake_form_lifestyle_next',
        [IntakeStep.AdvancedTraining]: 'intake_form_advanced_training_next',
        [IntakeStep.Schedule]: 'intake_form_schedule_next',
        [IntakeStep.NutritionOptions]: 'intake_form_nutrition_options_next',
        [IntakeStep.Goals]: 'intake_form_goals_next',
        [IntakeStep.TrainingVolume]: 'intake_form_training_volume_next',
        [IntakeStep.EnergyBalance]: 'intake_form_energy_balance_next',
      };
      const eventName = stepEventMap[stepBeforeGenerate];
      if (eventName) {
        this.trackIntakeStepEvent(eventName);
      }

      // call onNextClicked to validate and progress
      currentGroup.onNextClicked();

      // trigger change detection to update button disabled state
      this.changeDetector.markForCheck();
    }
  }

  onIntakeProgressChanged() {
    const newStep = this.intakeFormState.intakeFormProgress;
    // only accept step increases (user clicking Next) — reject init re-emits,
    // remote sync, and validation-triggered step recalculations
    // (vld-message setTimeout → onMessagesChanged → onProgressChanged can emit
    // a lower step after remoteUpdatePending was already consumed)
    if (newStep <= this.localIntakeStep || this.loading || this.remoteUpdatePending) return;
    this.localIntakeStep = newStep;
    // #4059: the first-workout handoff (step 12) has no inputs to trigger a later save, so
    // persist the step now — otherwise a reload or another device would resume at Program
    // Overview instead of the handoff (and the Start-CTA resume recompute would never run).
    if (newStep === IntakeStep.FirstWorkoutHandoff) {
      this.somethingChanged();
    }
    // other steps: position saved with the next input change via somethingChanged()
  }

  // option B coach onboarding (#3389): persist the client's intake
  // answers, mark the plan as awaiting the coach's finalization, queue
  // the intakeSubmitted notification to the coach, and route the client
  // to the awaiting-coach holding page.
  private async submitCoachedIntakeAndHoldAsync(): Promise<void> {
    this.trackIntakeStepEvent('intake_form_coached_submit');

    const loader = this.loadingCtrl.create({ content: LL.intake.loading.finishingPlan() });
    void loader.present();

    try {
      // persist what the client typed so the coach sees it after impersonation
      await this.userDb.saveIntakeFromStateAsync(DatabaseWrapper.getTransactionId());

      // flip the welcomePhase so app.component routes the client to the
      // holding page on the next render. saving the plan also kicks
      // PouchDB replication so the coach's database sees the new state.
      this.fitnessPlan.welcomePhase = WelcomePhase.AwaitingCoachFinalize;
      await this.userDb.savePlanAsync(DatabaseWrapper.getTransactionId());

      // queue the coach notification. clientSlotId comes from the
      // as-client doc written at claim time (coach-claim.service Step 7);
      // clientDisplayName mirrors the weekly-checkup pattern (auth name
      // with uid fallback).
      const relDoc = this.userDb.loadCoachRelAsClient();
      const clientSlotId = relDoc?.clientSlotId;
      if (clientSlotId) {
        const clientDisplayName = this.auth.user?.name || this.auth.user?.uid || '';
        await this.coachNotify.queueNotificationAsync({
          type: 'intakeSubmitted',
          clientDisplayName,
          clientSlotId,
        });
      } else {
        // the as-client doc should always carry the slot id for an
        // option-B claim (coach-claim writes it at claim time). track
        // so a real failure surfaces in Sentry while we still route the
        // client to the holding page so they are not stuck on the wizard.
        this.logging.trackSilentProblem('intake-form: coached submit with no clientSlotId in as-client doc', {});
      }
    } finally {
      void loader.dismiss();
    }

    // route to the home page; app.component's getPageByWelcomePhase
    // picks up the AwaitingCoachFinalize phase and lands on the
    // holding page.
    await this.navCtrl.setRootAsync(IHomePage, { tab: HomeTab.DailyLog });
  }

  handleFinishButton(event: Event) {
    this.trackIntakeStepEvent("intake_form_finish");
    lockClick(event, this.finishIntakeForm());
  }

  // #4059 first-workout handoff (step 12) — "Start your first workout". Finishes the
  // intake exactly like the normal finish, then opens the first workout session.
  handleStartFirstWorkout(event: Event) {
    this.trackIntakeStepEvent('intake_form_finish');
    this.trackIntakeStepEvent('intake_form_first_workout_start', { started_today: this.isFirstScheduledWorkoutToday });
    lockClick(event, this.finishIntakeForm('workout'));
  }

  // #4059 first-workout handoff (step 12) — "Finish, I'll start later". Same as the
  // pre-#4059 Finish: activate the plan and route to Home.
  handleFinishLater(event: Event) {
    this.trackIntakeStepEvent('intake_form_finish');
    lockClick(event, this.finishIntakeForm('home'));
  }

  // #4059: decide whether "Start your first workout" can launch a session. It reuses
  // the rest-day launch path (loadWorkoutForDate), which builds today's session from the
  // next scheduled workout as a template. Recomputed whenever the handoff is (re)shown —
  // the local 11->12 tap, a PWA reload/resume onto step 12, and a cross-device sync into
  // it — so the Start button is never lost. Does NOT emit telemetry; the "handoff shown"
  // event fires once at the 11->12 tap in handleNextButton.
  private async computeFirstWorkoutStartabilityAsync(): Promise<void> {
    const now = this.debug.getNowLocal();
    try {
      const { declaredState } = await this.loadStatesForGenerationAsync();
      if (this.viewDestroyed) return;
      const rules = await this.getScheduleRulesForPreviewAsync(declaredState);
      if (this.viewDestroyed) return;
      // match loadWorkoutForDate's window exactly: it searches [tomorrow, today+7] and
      // EXCLUDES today. Using the same window means a visible Start button always has a
      // template to launch — it never trips loadWorkoutForDate's "no next workout" guard.
      this.hasStartableFirstWorkout = !!getNextWorkoutDateISO(rules, now.plus({ days: 1 }), 6);
      // separately (telemetry only): is today itself a scheduled training day?
      this.isFirstScheduledWorkoutToday = getNextWorkoutDateISO(rules, now) === now.toISODate();
    } catch (err) {
      // if the schedule can't be resolved, fail safe: hide Start so the user always
      // has the working "Finish, I'll start later" path. surface so the cause is logged.
      this.hasStartableFirstWorkout = false;
      this.isFirstScheduledWorkoutToday = false;
      this.logging.trackSilentProblem('intake-form: first-workout startability check failed', { code: (err as any)?.code, message: (err as any)?.message });
    }
    if (this.viewDestroyed) return;
    this.changeDetector.markForCheck();
  }

  shouldOfferGeneratePlan() {
    return (this.mode === 'new' || this.mode === 'change') && !deepCompareEQ(this.intakeFormState, this.generatedPlanIntakeFormState);
  }

  shouldOfferFinishButton() {
    return this.generatedFitnessPlan;
  }

  isNextButtonDisabled(): boolean {
    if (!this.vldProgress || !this.vldProgress.groups) {
      return false;
    }

    const currentGroup = this.vldProgress.groups.find(
      g => g.stepNumber === this.intakeFormState.intakeFormProgress
    );

    if (!currentGroup) {
      return false;
    }

    // button should be disabled when state is Errors
    return currentGroup.state === VldGroupState.Errors;
  }

  async waitForAnimationsRecursive(target: HTMLElement) {

    if (target.style.transition) {
      await waitHtmlEvent(target, 'transitionend');
    }
    if ('getAnimations' in target) {
      const animations = (target as any).getAnimations();
      if (animations.length > 0) {
        // Catch cancellation errors - animations can be cancelled during navigation
        await Promise.all(animations.map(a => a.finished.catch(() => {})));
      }
    }

    for (let iChild = 0; iChild < target.children.length; iChild++) {
      const child = target.children[iChild];
      if (child instanceof HTMLElement) {
        await this.waitForAnimationsRecursive(child);
      }
    }
  }

  onProgressionTestingLevelChanged() {
    this.settings.progressionTesting_Intensity = IntensityLevel.Initial;
  }

  canSkipBodyfatEstimate() {
    return this.mode !== 'new' && this.lastBFMethod && this.lastFitnessStateBf_pc;
  }

  async logIntakeFormAsync() {
    await this.userDb.createIntakeFormStateLogAsync(this.intakeFormState, DatabaseWrapper.getTransactionId());
  }

  async clearIntakeFormAsync() {
    // preserve maxEffortPlan and its snapshot before clearing
    // this allows us to reuse them in future sessions if data hasn't changed
    const savedMaxEffortPlan = this.intakeFormState.maxEffortPlan;
    const savedMaxEffortPlanSnapshot = this.intakeFormState.maxEffortPlanIntakeFormState;

    this.userDb.clearIntakeFormState(this.intakeFormState);

    // only restore maxEffortPlan if it exists
    // if it's undefined, let initializeAsync regenerate it
    if (savedMaxEffortPlan) {
      this.intakeFormState.maxEffortPlan = savedMaxEffortPlan;
      this.intakeFormState.maxEffortPlanIntakeFormState = savedMaxEffortPlanSnapshot;
    }

    await this.userDb.saveIntakeFromStateAsync(DatabaseWrapper.getTransactionId());
  }

  // Build the (declaredState, fitnessState, immutableState) trio used for any
  // plan-generation / nutrition-preview / edit-workouts call. In coach mode
  // these MUST come from the slot's intakeFormState alone — never from the
  // coach's userDb singletons. Non-coach mode loads from the user's own DB
  // and overlays intake-form changes (#3289).
  private async loadStatesForGenerationAsync(): Promise<{ declaredState: DeclaredState, fitnessState: FitnessState, immutableState: ImmutableState }> {
    let declaredState: DeclaredState;
    let fitnessState: FitnessState;
    let immutableState: ImmutableState;
    if (this.isCoachMode) {
      declaredState = {} as DeclaredState;
      fitnessState = this.userDb.getEmptyFitnessState();
      immutableState = {} as ImmutableState;
    } else {
      [declaredState, fitnessState, immutableState] = deepClone(await Promise.all([
        this.userDb.loadDeclaredStateAsync(this.docWatcher),
        this.userDb.loadFitnessStateAsync(this.docWatcher),
        this.userDb.loadImmutableStateAsync(this.docWatcher),
      ]));
    }
    this.fillDeclaredState(declaredState);
    this.fillFitnessState(fitnessState);
    this.fillImmutableState(immutableState);
    // #4591: a workout split the user does not train often enough for would make
    // the solver refuse outright, and a plan that will not build is not an
    // acceptable thing for a real user to run into. Hand the solver Optimal
    // instead. This is safe to write here because everything above is a COPY —
    // never the loaded singleton — so the user's own choice stays on disk and
    // comes back by itself if they raise their training days again.
    declaredState.trainingSplitMode = resolveEffectiveSplitMode(
      resolveTrainingSplitMode(declaredState),
      getWorkoutsPerWeek(declaredState),
    );
    return { declaredState, fitnessState, immutableState };
  }

  fillImmutableState(immutableState: ImmutableState) {
    // also fire regardless of `mode` when the answers live only in the intake form
    // (see areAnswersOnlyInIntakeForm) — this is handed an empty container, and the
    // born/gender/height the client entered exist nowhere else. Without projecting
    // them the generated plan's solver reads an empty immutable state and throws.
    if (this.mode === 'new' || this.areAnswersOnlyInIntakeForm()) {
      Object.assign(immutableState, mapIntakeFormToImmutableState(this.intakeFormState));
    }
  }

  async fillAndSaveImmutableState() {
    // #4139: this is the ONLY writer of the immutable singleton (born/gender/height),
    // so a first-plan run must persist it here or nowhere. This was the site that got
    // missed: the coached finalize runs with mode='change', so before isBuildingFirstPlan()
    // covered it the method returned early and left immutable empty — `isIntakeDone(immutable)`
    // stayed false (no Mixpanel profile props) and a later change-mode open pre-filled
    // born/gender/height from the empty doc, wiping them. The sibling declared/fitness
    // writers below have no mode gate at all, so they already persisted; this one did not.
    // The coached review is still in progress here — the finish has not yet flipped
    // welcomePhase to Done — so isBuildingFirstPlan() is still true.
    if (this.isBuildingFirstPlan()) {
      const immutableState = await this.userDb.loadImmutableStateAsync(this.docWatcher);
      this.fillImmutableState(immutableState);
      return this.userDb.saveImmutableStateAsync(DatabaseWrapper.getTransactionId());
    }
  }

  fillDeclaredState(declaredState: DeclaredState) {
    // selective merge: only copy defined values from intakeFormState to declaredState
    // — prevents stale/incomplete intakeFormState from overwriting valid DB values with undefined
    // (not using deepOverwriteCouchSafe here because we specifically need to skip undefined values)
    // seed new-user rest-timer defaults only on a first-plan run; an edit-mode re-save
    // must not migrate an existing user's flag. #4139: this matches the option-A
    // coach-claim path, where coach-claim.service passes seedNewUserDefaults:true at
    // finalize.
    const mapped = mapIntakeFormToDeclaredState(this.intakeFormState, { seedNewUserDefaults: this.isBuildingFirstPlan() });
    for (const key of Object.keys(mapped)) {
      if (mapped[key] !== undefined) {
        declaredState[key] = mapped[key];
      }
    }
    // if primary goal changed, clear custom target values (client-only concern)
    if (this.intakeFormState.trainingGoalGrowth !== this.lastTrainingGoalGrowth) {
      declaredState.targetBodyfat_pc = undefined;
      declaredState.targetBodyWeight_kg = undefined;
    }
  }

  async fillAndSaveDeclaredState() {
    let declaredState = await this.userDb.loadDeclaredStateAsync(this.docWatcher);
    this.fillDeclaredState(declaredState);
    return this.userDb.saveDeclaredStateAsync(DatabaseWrapper.getTransactionId());
  }

  shouldAskForDailySchedule() {
    return this.mode === 'new';
  }

  async shouldReadjustDefaultSchedule() {
    let declaredState = await this.userDb.loadDeclaredStateAsync(this.docWatcher);
    return this.hasMealFrequencyChanged(declaredState) || !declaredState.trainingDays.sequentialEqual(this.intakeFormState.trainingDays);
  }

  /**
   * #3382: true when the in-progress meal frequency differs from the currently-applied
   * (persisted) one. Shared by shouldReadjustDefaultSchedule (schedule re-adjust) and the
   * keep-path nutrition repartition, so the meal-frequency-change check lives in one place.
   */
  private hasMealFrequencyChanged(appliedDeclaredState: DeclaredState): boolean {
    return appliedDeclaredState.mealFrequency !== this.intakeFormState.mealFrequency;
  }

  /**
   * #3635: true when the in-progress energy balance (intakeDeltaPercent) differs from the
   * currently-applied (persisted) one. Energy balance is exempt from needsPlanRegeneration,
   * so it can silently reach the keep/volumes-only paths.
   */
  private hasEnergyBalanceChanged(appliedDeclaredState: DeclaredState): boolean {
    return appliedDeclaredState.intakeDeltaPercent !== this.intakeFormState.intakeDeltaPercent;
  }

  fillFitnessState(fitnessState: FitnessState) {
    if (this.intakeFormState.bfData.bf_pc !== undefined) {
      fitnessState.bfData = this.intakeFormState.bfData;
      fitnessState.lastBfDataUpdate_utc = this.debug.getNowLocal().toUTC().toISO();
    }
    // also fire regardless of `mode` when the answers live only in the intake form
    // (see areAnswersOnlyInIntakeForm) — same reason as fillImmutableState: the
    // client's strength data must be projected onto the empty fitnessState container
    // during plan generation, including a coach re-edit (#3289).
    if (this.shouldCheckForDeclaredStrength() || this.areAnswersOnlyInIntakeForm()) {
      fitnessState.exerciseStats = this.intakeFormState.exerciseStats;
    }
    if (this.shouldCheckForBodyWeight() && this.intakeFormState.weight_kg !== undefined) {
      fitnessState.weight_kg = this.intakeFormState.weight_kg;
    }
  }

  async fillAndSaveFitnessState() {
    const transaction = DatabaseWrapper.getTransactionId();
    const fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);
    this.fillFitnessState(fitnessState);

    // df-5556257f1229: carry what the user changed in Edit Workouts on top of what their
    // intake answers just wrote. Edit Workouts saves nothing itself during intake — it hands
    // its fitness state back through onTrainingProgramEdited — so this is the only place the
    // rep-target, myo-reps and RM-testing preferences they set in there can ever be stored.
    // The merge is what keeps both halves; the helper says why neither copy may simply win.
    const choice = chooseFitnessStateToPersistAtFinish(
      this.fitnessStateBeforeEditWorkouts, fitnessState, this.fitnessState);
    if (choice.state !== fitnessState) {
      // write the merged content ONTO the loaded document rather than saving the merged
      // object: the database saves whichever object it holds for this singleton, and
      // deepOverwriteCouchSafe keeps that object's _id/_rev so the save is not a conflict
      deepOverwriteCouchSafe(fitnessState, choice.state);
    }
    if (choice.guessedFieldCount > 0 || choice.mergedWithoutBase) {
      // an unresolved condition, not a repair: the merge could not settle a field from the
      // shared starting point and guessed. Worth reading the count's own documentation before
      // acting on this — a guess is NOT always a dropped value; one of the two shapes that
      // reach it keeps the surviving value. Nothing else can see that this happened.
      this.logging.trackSilentProblem(
        'intake: finish fitness state merge guessed',
        {
          transaction,
          guessedFieldCount: choice.guessedFieldCount,
          mergedWithoutBase: choice.mergedWithoutBase,
        });
    }

    return this.userDb.saveFitnessStateAsync(transaction);
  }

  async shouldMarkPlanForInitializeBaseline() {
    // #4139: a first-plan run is a baseline generation by definition. Return true early —
    // this also avoids the declaredState read below, whose trainingDays would be
    // undefined for a pre-finalize client and make the .filter throw.
    if (this.isBuildingFirstPlan()) {
      return true;
    }

    let [declaredState, fitnessState] = await Promise.all([this.userDb.loadDeclaredStateAsync(this.docWatcher), this.userDb.loadFitnessStateAsync(this.docWatcher)]);
    if (declaredState.trainingDays.filter(e => e === true).length !== this.intakeFormState.trainingDays.filter(e => e === true).length) {
      return true;
    }

    if (declaredState.trainingVolumeWeeklyAvg !== this.intakeFormState.trainingVolumeWeeklyAvg) {
      return true;
    }

    if (declaredState.intakeDeltaPercent !== this.intakeFormState.intakeDeltaPercent) {
      return true;
    }

    if (this.hasGoalDirectionChanged(declaredState, fitnessState)) {
      return true;
    }

    return false;
  }

  /**
   * True when the in-progress training goal points the opposite way to the applied one —
   * fat loss becoming muscle growth, or the reverse. The goal is declared indirectly (the
   * same declared goal reads as growth or as fat loss depending on body fat), so the two
   * sides are resolved through isUsersTrainingGoalGrowth rather than compared directly.
   *
   * A flip makes any existing daily calorie target wrong in direction, not just in size,
   * which is why the Update Your Program screen warns about keeping one across it (#4973).
   */
  private hasGoalDirectionChanged(appliedDeclaredState: DeclaredState, appliedFitnessState: FitnessState): boolean {
    const currentBf_pc = this.intakeFormState.bfData.bf_pc || appliedFitnessState.bfData.bf_pc;
    if (currentBf_pc === undefined) {
      return false;
    }
    return isUsersTrainingGoalGrowth(appliedDeclaredState.trainingGoalGrowth, this.intakeFormState.gender, appliedFitnessState.bfData.bf_pc)
      !== isUsersTrainingGoalGrowth(this.intakeFormState.trainingGoalGrowth, this.intakeFormState.gender, currentBf_pc);
  }

  /**
   * True when something that actually determines the daily calorie target differs from what
   * is applied. Shared by two callers that must agree: the re-solve on a kept plan, and the
   * warning on the Update Your Program screen that names the number about to replace the
   * user's own. If they disagreed the screen would promise a replacement that never happens.
   */
  private async hasTargetAffectingChangeAsync(appliedDeclaredState: DeclaredState, appliedFitnessState: FitnessState): Promise<boolean> {
    return await this.shouldMarkPlanForInitializeBaseline()
      || this.hasBodyDataChanged(appliedFitnessState)
      || this.hasActivityLevelChanged(appliedDeclaredState);
  }

  /**
   * True when the in-progress activity level differs from the applied one.
   *
   * It feeds the physical-activity factor the solver multiplies into maintenance calories, so
   * it changes what the daily target should be — which is why "update my calorie target" has to
   * act on it, and why keeping a target across it is worth warning about. It is deliberately
   * NOT part of shouldMarkPlanForInitializeBaseline, which decides something else (whether the
   * plan's baseline is stale enough to rebuild), and widening that would change the rebuild
   * path this branch is not otherwise touching.
   */
  private hasActivityLevelChanged(appliedDeclaredState: DeclaredState): boolean {
    return this.intakeFormState.activityLevel !== undefined
      && appliedDeclaredState.activityLevel !== this.intakeFormState.activityLevel;
  }

  /**
   * True when the in-progress body weight or body fat differs from the applied one. The
   * daily calorie target is worked out from both, so keeping an existing target across such
   * a change leaves it calculated from a body the user no longer has (#4973).
   */
  private hasBodyDataChanged(appliedFitnessState: FitnessState): boolean {
    const weightChanged = this.intakeFormState.weight_kg !== undefined
      && this.intakeFormState.weight_kg !== appliedFitnessState.weight_kg;
    const bodyFatChanged = this.intakeFormState.bfData?.bf_pc !== undefined
      && this.intakeFormState.bfData.bf_pc !== appliedFitnessState.bfData?.bf_pc;
    return weightChanged || bodyFatChanged;
  }

  /**
   * The daily calorie number the solver would work out for the settings now on the form,
   * with the user's own overrides taken off. Shown on the Update Your Program screen as the
   * number that would replace a target the user typed in.
   *
   * `undefined` when there is no such number to give -- the Update Your Program screen then warns
   * that the target will be replaced without naming what it will be replaced by, rather than
   * printing a figure nobody computed (df-f3070b96b839).
   */
  private async computeRecommendedDailyIntakeAsync(appliedPlan: FitnessPlan): Promise<number | undefined> {
    const { declaredState, fitnessState, immutableState } = await this.loadStatesForGenerationAsync();
    const scheduleRules = await this.getScheduleRulesForPreviewAsync(declaredState);
    // Mark the baseline stale on a COPY before previewing. computeRecommendedNutrition only
    // restores the pre-override number and re-solves, and a solve recomputes the target from
    // the settings only on the stale-baseline branch — so without this the preview reports
    // the OLD baseline, while answering Update would build a different number from the new
    // settings. Nothing marks the live plan itself (#4602), so this copy is the only mark the
    // preview gets, on every edit. The copy keeps the live plan untouched.
    const planForPreview = deepClone(appliedPlan);
    planForPreview.shouldInitializeBaselineAtNextReplan = true;
    const recommended = computeRecommendedNutrition(
      planForPreview, declaredState, immutableState, fitnessState, scheduleRules, this.debug.getNowLocal(), this.logging);
    return recommended?.dailyIntake;
  }

  async refreshWorkoutsList() {
    const checkedPlan = this.generatedFitnessPlan;
    if (checkedPlan) {
      const { declaredState, fitnessState } = await this.loadStatesForGenerationAsync();

      // df-b9d12c28a387: whatever ran during that await may have replaced or cleared the
      // program. if replaced, the refresh that installed the newer one draws it. if cleared,
      // drawing would throw on the missing program, so skip it and report the condition
      const redraw = decideOverviewRedraw(checkedPlan, this.generatedFitnessPlan);
      if (redraw === OverviewRedraw.SkipCleared) {
        this.logging.trackSilentProblem('intake: program cleared while the overview was loading', {
          mode: this.mode,
          currentView: this.currentView,
          skipPlanRegeneration: this.skipPlanRegeneration,
        });
      }
      if (redraw !== OverviewRedraw.Draw) {
        return;
      }

      // Store the raw training data for the component to process
      this.trainingData = {
        fitnessPlan: checkedPlan,
        declaredState,
        fitnessState: this.fitnessStateForOverviewDisplay(fitnessState),
      };

      // generate program overview for display
      await this.refreshProgramOverviewAsync();

      this.changeDetector.markForCheck();
    }
  }

  /**
   * Which copy of the fitness state the program overview is DRAWN from. #5280.
   *
   * Edit Workouts saves nothing during intake — it hands its edited copy back through
   * onTrainingProgramEdited, which parks it in `this.fitnessState` until Finish stores it.
   * The database still holds the copy from before that visit, so drawing the overview from
   * the database shows the user preferences they have just changed away from.
   *
   * The test is the one the FINISH uses, on purpose — see chooseFitnessStateToPersistAtFinish
   * in intake-finish-fitness-state.ts — so display and storage cannot decide differently about
   * whether an edit exists. Keying on the snapshot rather than on `fitnessState` also means a
   * cancel turns this off without clearing that field, which is NOT safe to clear.
   *
   * Only the OVERVIEW is redirected; every re-solve keeps reading the database copy through
   * loadStatesForGenerationAsync. Why that is right, why clearing the field on cancel crashes,
   * and the measurement showing no re-solve reads anything Edit Workouts can write:
   * `design-docs/sync-integrity.md`, "Which fitness state the intake summary draws from".
   */
  private fitnessStateForOverviewDisplay(loadedForGeneration: FitnessState): FitnessState {
    // if Edit Workouts has run and its result is still waiting for Finish, draw from that
    if (this.fitnessStateBeforeEditWorkouts !== undefined && this.fitnessState) {
      return this.fitnessState;
    }
    return loadedForGeneration;
  }

  updateDedicationSliders() {

    if (this.intakeFormState.maxEffortPlan) {
      let trainingVolumeSliderTrackBackgroundGradientMarks: CsSliderBackgroundGradientMark[] = [
        { value: this.getTrainingVolumeForDedicationLevel(DedicationLevel.SlowAndSteady) - 2, color: 'yellow' },
        { value: this.getTrainingVolumeForDedicationLevel(DedicationLevel.SlowAndSteady) + 1, color: 'green' },
        { value: this.getTrainingVolumeForDedicationLevel(DedicationLevel.MaximumEffort), color: 'green' },
        { value: this.getTrainingVolumeForDedicationLevel(DedicationLevel.MaximumEffort) + 2, color: 'yellow' },
        { value: this.getTrainingVolumeOverboardLimit(), color: 'yellow' },
        { value: this.getTrainingVolumeOverboardLimit() + 2, color: 'red' },
      ]

      let trainingVolumeSliderHighlightedValues: number[] = [
        // Math.round(this.getTrainingVolumeForDedicationLevel(DedicationLevel.SlowAndSteady)),
        // Math.round(this.getTrainingVolumeForDedicationLevel(DedicationLevel.Balanced)),
        // Math.round(this.getTrainingVolumeForDedicationLevel(DedicationLevel.MaximumEffort)),
        this.getSuggestedTrainingVolume()
      ];
      this.trainingVolumeSliderHighlightedValues = trainingVolumeSliderHighlightedValues;

      // sort and remove duplicates
      trainingVolumeSliderTrackBackgroundGradientMarks = trainingVolumeSliderTrackBackgroundGradientMarks.sort((a, b) => a.value - b.value);
      trainingVolumeSliderTrackBackgroundGradientMarks = trainingVolumeSliderTrackBackgroundGradientMarks.filter((value, index) => trainingVolumeSliderTrackBackgroundGradientMarks.findIndex((value2) => value2.color === value.color && value2.value === value.value) === index);
      this.trainingVolumeSliderTrackBackgroundGradientMarks = trainingVolumeSliderTrackBackgroundGradientMarks;

      let slowAndSteadyIntakeDelta = roundToPrecision(constraintIntakeDeltaPercentForDedicationLevel(this.intakeFormState.maxEffortPlan.intakeDeltaPercent, DedicationLevel.SlowAndSteady) * 100, 0.1);
      let maxEffortIntakeDelta = roundToPrecision(constraintIntakeDeltaPercentForDedicationLevel(this.intakeFormState.maxEffortPlan.intakeDeltaPercent, DedicationLevel.MaximumEffort) * 100, 0.1);

      let intakeDeltaPercentSliderHighlightedValues: number[] = [
        // this.getIntakeDeltaPercentForDedicationLevel(DedicationLevel.SlowAndSteady),
        // this.getIntakeDeltaPercentForDedicationLevel(DedicationLevel.Balanced),
        // this.getIntakeDeltaPercentForDedicationLevel(DedicationLevel.MaximumEffort),
        this.getSuggestedIntakeDeltaPercent()
      ];
      this.intakeDeltaPercentSliderHighlightedValues = intakeDeltaPercentSliderHighlightedValues;

      let intakeDeltaPercentSliderTrackBackgroundGradientMarks: CsSliderBackgroundGradientMark[] = [
        { value: 0, color: 'green' },
        //{ value: slowAndSteadyIntakeDelta - Math.sign(slowAndSteadyIntakeDelta) * 2, color: 'yellow' },
        { value: slowAndSteadyIntakeDelta, color: 'green' },
        { value: maxEffortIntakeDelta, color: 'green' },
        { value: maxEffortIntakeDelta + Math.sign(slowAndSteadyIntakeDelta) * 2, color: 'yellow' },
        { value: this.getDeltaPercentOverboardLimit() - Math.sign(maxEffortIntakeDelta) * 1, color: 'yellow' },
        { value: this.getDeltaPercentOverboardLimit() + Math.sign(maxEffortIntakeDelta) * 2, color: 'red' },
      ]
      intakeDeltaPercentSliderTrackBackgroundGradientMarks = intakeDeltaPercentSliderTrackBackgroundGradientMarks.sort((a, b) => a.value - b.value);
      intakeDeltaPercentSliderTrackBackgroundGradientMarks = intakeDeltaPercentSliderTrackBackgroundGradientMarks.filter((value, index) => intakeDeltaPercentSliderTrackBackgroundGradientMarks.findIndex((value2) => value2.color === value.color && value2.value === value.value) === index);
      this.intakeDeltaPercentSliderTrackBackgroundGradientMarks = intakeDeltaPercentSliderTrackBackgroundGradientMarks;
    }

    // update badge colors after gradient marks are set
    setTimeout(() => {
      this.updateTrainingVolumeBadgeColor();
      this.updateIntakeDeltaBadgeColor();
    }, 0);

    this.changeDetector.markForCheck();
  }

  /**
   * @param nutritionChoice what the user answered about their daily calorie target on the
   *   Update Your Program screen. Only consulted when building the REAL plan: the max-effort
   *   plan is a throwaway preview used to suggest values, so it always works nutrition out
   *   from scratch (#4973).
   */
  async generateProgramAsync(generateMaxEffortPlan: boolean = false, nutritionChoice: NutritionUpdateChoice = NutritionUpdateChoice.Update): Promise<boolean> {
    let loader = this.loadingCtrl.create({
      content: generateMaxEffortPlan ? LL.intake.loading.analyzingFitness() : LL.intake.loading.generatingProgram(),
    });

    // outer try/finally guarantees loader dismiss + wake-lock release on every
    // exit path — including throws from the pre-solver awaits and any future
    // early returns. The
    // inner try/catch keeps the original solver-failure UX (subscription
    // alert / generic "Couldn't generate plan" alert).
    try {
      // hold screen awake for the solveWorkouts round-trip (tens of seconds;
      // UI copy says "may take a minute or two"). #3366 secondary concern.
      // Inside the try so finally is the single release site.
      void this.wakeLock.acquireAsync('intake:generate-plan');
      void loader.present();

      // finish up misc calculations
      this.calculateOverallTrainingLevel();

      let fitnessPlan: FitnessPlan;
      // The live plan is NOT marked for a fresh calorie target here. Marking it put the mark
      // on the stored plan's in-memory copy before the user had answered anything; when they
      // cancelled, the next unrelated save of the plan stored the mark, and the weekly checkup
      // then reported "progress undetermined" for weeks (#4602). Every path that wants a fresh
      // target already gets one without it: a rebuild clears the nutrition anchor inside
      // generateFitnessPlanAsync, the max-effort pass below marks its own generated plan, and
      // resolveNutritionForKeptPlanIfNeeded marks its own clone. The user can decline a fresh
      // target on the Update Your Program screen, in which case a rebuild keeps it (#4973).
      const keepNutritionBaseline = !generateMaxEffortPlan && nutritionChoice === NutritionUpdateChoice.Keep;

      let fakePlan: FakePlanParams = null;
      if (this.settings.testMode && this.settings.progressionTesting) {
        fakePlan = {
          slot: this.settings.progressionTesting_Slot,
          sets: this.settings.progressionTesting_Sets,
          level: this.settings.progressionTesting_Level,
          otherLevel: this.settings.progressionTesting_otherLevel,
          intensity: this.settings.progressionTesting_Intensity,
          slotIntensities: this.settings.progressionTesting_SlotIntensities ? JSON.parse(this.settings.progressionTesting_SlotIntensities) : undefined,
        };
      }

      // ask the server to calculate advancement, etc from it
      try {
        let transaction = DatabaseWrapper.getTransactionId();
        await this.svPlanHandler.handleIntakeFormAsync(transaction);
        const { declaredState, fitnessState, immutableState } = await this.loadStatesForGenerationAsync();
        // when the answers live only in the intake form (see areAnswersOnlyInIntakeForm)
        // there is no prior plan worth seeding from: in coach mode the plan on hand is the
        // COACH's (#3289), and for a coached review the client's pre-finalize currentplan is
        // an empty AwaitingCoachFinalize placeholder that was never generated. Progressing
        // from that placeholder feeds the solver partial meal-time fields (NaN) and
        // generation fails (#4139). So seed the solver from an empty plan instead.
        const planGenOptions = this.areAnswersOnlyInIntakeForm() ? { existingPlan: this.userDb.getEmptyPlan() } : {};
        if (generateMaxEffortPlan) {
          delete declaredState.trainingVolumeWeeklyAvg;
          delete declaredState.intakeDeltaPercent;
          delete this.intakeFormState.trainingVolumeWeeklyAvg;
          delete this.intakeFormState.intakeDeltaPercent;
          fitnessPlan = await this.svPlanHandler.generateFitnessPlanAsync(this.mode, declaredState, fitnessState, immutableState, { ...planGenOptions, fakePlan: fakePlan, maxEffort: true });
          await this.svPlanHandler.markPlanForInitializeBaseline(fitnessPlan);
          await this.svPlanHandler.updatePlanAsync(fitnessPlan, declaredState, fitnessState, immutableState);
        } else {
          fitnessPlan = await this.svPlanHandler.generateFitnessPlanAsync(this.mode, declaredState, fitnessState, immutableState, { ...planGenOptions, fakePlan: fakePlan, keepNutritionBaseline });
          await this.svPlanHandler.updatePlanAsync(fitnessPlan, declaredState, fitnessState, immutableState);
        }

        if (generateMaxEffortPlan) {
          // ignoreMap drops the snapshot key itself so it never nests into its own snapshot (#3405)
          this.intakeFormState.maxEffortPlanIntakeFormState = deepClone(this.intakeFormState, { ignoreMap: { maxEffortPlanIntakeFormState: true } });
          this.intakeFormState.maxEffortPlan = fitnessPlan;
          this.intakeFormState.trainingVolumeWeeklyAvg = this.getSuggestedTrainingVolume();
          this.intakeFormState.intakeDeltaPercent = this.getSuggestedIntakeDeltaPercent();

          // pre-advance the step so somethingChanged() saves the correct step;
          // onNextClicked() fires after this returns but the save must already
          // include TrainingVolume so remote devices land on the right step
          if (this.intakeFormState.intakeFormProgress === IntakeStep.Goals) {
            this.intakeFormState.intakeFormProgress = IntakeStep.TrainingVolume;
            this.localIntakeStep = IntakeStep.TrainingVolume;
          }

          this.updateDedicationSliders();

          this.somethingChanged();
        } else {
          this.installGeneratedPlan(fitnessPlan);
          void this.refreshWorkoutsList();

          // pre-advance the step so somethingChanged() saves the correct step;
          // onNextClicked() fires after this returns but the save must already
          // include ProgramOverview so remote devices land on the right step
          if (this.intakeFormState.intakeFormProgress === IntakeStep.EnergyBalance) {
            this.intakeFormState.intakeFormProgress = IntakeStep.ProgramOverview;
            this.localIntakeStep = IntakeStep.ProgramOverview;
          }

          this.somethingChanged();
        }

        const scheduleRules = await this.getScheduleRulesForPreviewAsync(declaredState);

        // update nutrition card data using the generated plan
        const planToUse = generateMaxEffortPlan ? fitnessPlan : this.generatedFitnessPlan;
        this.applyNutritionData(planToUse, declaredState, immutableState, fitnessState, scheduleRules);
      } catch (err) {
        // if subscription is not active, redirect to subscribe page
        if (isWorkoutSolverRefused(err)) {
          void this.alertCtrl.showAlertAsync({
            message: LL.intake.errors.subscriptionRequired(),
            buttons: [{
              text: LL.common.buttons.ok(),
              role: AlertButtonRole.Primary,
              result: () => {
                void this.navCtrl.setRootAsync(ISubscribePage, {});
              },
            }],
          });
          this.changeDetector.markForCheck();
          return false;
        }

        this.logging.trackAbnormalException(err, LL.intake.errors.couldntGeneratePlan());
        void this.alertCtrl.showAlertAsync({
          message: LL.intake.errors.couldntGeneratePlan(),
          buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
        });
        this.changeDetector.markForCheck();
        return false;
      }

      this.changeDetector.markForCheck();
      return true;
    } finally {
      void loader.dismiss();
      void this.wakeLock.releaseAsync('intake:generate-plan');
    }
  }

  getTrainingVolumeOverboardLimit() {
    return Math.round(this.intakeFormState.maxEffortPlan.trainingVolumeWeeklyAvg * 1.3);
  }

  getDeltaPercentOverboardLimit() {
    let maxEffortIntakeDelta = roundToPrecision(constraintIntakeDeltaPercentForDedicationLevel(this.intakeFormState.maxEffortPlan.intakeDeltaPercent, DedicationLevel.MaximumEffort) * 100, 0.1);
    return (maxEffortIntakeDelta + Math.sign(maxEffortIntakeDelta) * 10);
  }

  getTrainingVolumeForDedicationLevel(dedicationLevel: DedicationLevel) {
    return getTrainingVolumeForDedicationLevel(this.intakeFormState, dedicationLevel);
  }

  getSelectedTrainingDedicationLevel() {
    return getSelectedTrainingDedicationLevel(this.intakeFormState);
  }

  getSuggestedTrainingDedicationLevel() {
    return getSuggestedTrainingDedicationLevel(this.intakeFormState);
  }

  getSuggestedTrainingVolume() {
    return Math.round(this.getTrainingVolumeForDedicationLevel(this.getSuggestedTrainingDedicationLevel()));
  }

  // ---- workout split (#4591); see design-docs/training-splits.md.

  /**
   * Can this workout split be built for the number of days the user trains?
   *
   * The training days live on an earlier step of the form, so this is
   * re-evaluated on every render — going back and changing them updates which
   * options are available straight away.
   */
  isTrainingSplitAvailable(mode: TrainingSplitMode): boolean {
    return (this.intakeFormState.trainingFrequency || 0) >= getMinimumFrequencyForSplitMode(mode);
  }

  /**
   * The single line shown under a workout-split option.
   *
   * An option the user trains often enough for describes what that split is, in
   * Menno's own words (2026-08-13). An option they do NOT train often enough for
   * shows what would make it available instead, because at that point the reason
   * they cannot pick it matters more than what it would have done. Only ever one
   * line, never both.
   *
   * (An earlier round removed these descriptions entirely, on the grounds that
   * serious lifters already know what these splits are. Menno reversed that after
   * a call and supplied the wording himself, so a reader finding this and wanting
   * to "restore" the shorter version should check with him first.)
   */
  getTrainingSplitNote(mode: TrainingSplitMode): TranslatedString {
    if (!this.isTrainingSplitAvailable(mode)) {
      return LL.intake.trainingSplit.availableAtFrequency({ minimum: getMinimumFrequencyForSplitMode(mode) });
    }
    switch (mode) {
      case TrainingSplitMode.PushPullLegs:
        return LL.intake.trainingSplit.pushPullLegsNote();
      case TrainingSplitMode.UpperLower:
        return LL.intake.trainingSplit.upperLowerNote();
      case TrainingSplitMode.FullBody:
        return LL.intake.trainingSplit.fullBodyNote();
      default:
        return LL.intake.trainingSplit.optimalNote();
    }
  }

  trainingSplitModeChanged(mode: TrainingSplitMode) {
    this.intakeFormState.trainingSplitMode = mode;
    this.somethingChanged();
    this.changeDetector.markForCheck();
  }

  trainingDedicationLevelChanged(dedicationLevel: DedicationLevel) {
    this.intakeFormState.trainingVolumeWeeklyAvg = Math.round(this.getTrainingVolumeForDedicationLevel(dedicationLevel));
    this.somethingChanged();

    let dedicationLevelText = '';
    switch (dedicationLevel) {
      case DedicationLevel.Balanced:
        dedicationLevelText = 'balanced';
        break;
      case DedicationLevel.MaximumEffort:
        dedicationLevelText = 'maximum_effort';
        break;
      case DedicationLevel.SlowAndSteady:
        dedicationLevelText = 'slow_and_steady';
        break;
      default:
    }

    this.changeDetector.markForCheck();
    this.trackIntakeStepEvent('intake_form_training_volume_dedication', { value: dedicationLevelText });
  }

  getIntakeDeltaPercentForDedicationLevel(dedicationLevel: DedicationLevel) {
    if (dedicationLevel === DedicationLevel.SlowAndSteady) {
      return roundToPrecision(constraintIntakeDeltaPercentForDedicationLevel(this.intakeFormState.maxEffortPlan.intakeDeltaPercent, DedicationLevel.SlowAndSteady) * 100, 0.1)
    } else if (dedicationLevel === DedicationLevel.MaximumEffort) {
      return roundToPrecision(constraintIntakeDeltaPercentForDedicationLevel(this.intakeFormState.maxEffortPlan.intakeDeltaPercent, DedicationLevel.MaximumEffort) * 100, 0.1)
    } else {
      return roundToPrecision(constraintIntakeDeltaPercentForDedicationLevel(this.intakeFormState.maxEffortPlan.intakeDeltaPercent, DedicationLevel.Balanced) * 100, 0.1)
    }
  }

  getSelectedNutritionDedicationLevel(): DedicationLevel | undefined {
    return getSelectedNutritionDedicationLevel(this.intakeFormState);
  }

  nutritionDedicationLevelChanged(dedicationLevel: DedicationLevel) {
    this.intakeFormState.intakeDeltaPercent = this.getIntakeDeltaPercentForDedicationLevel(dedicationLevel);
    this.somethingChanged();

    let dedicationLevelText = '';
    switch (dedicationLevel) {
      case DedicationLevel.Balanced:
        dedicationLevelText = 'balanced';
        break;
      case DedicationLevel.MaximumEffort:
        dedicationLevelText = 'maximum_effort';
        break;
      case DedicationLevel.SlowAndSteady:
        dedicationLevelText = 'slow_and_steady';
        break;
      default:
    }
    this.trackIntakeStepEvent('intake_form_energy_balance_dedication', { value: dedicationLevelText });
  }

  getSuggestedNutritionDedicationLevel() {
    return DedicationLevel.Balanced;
  }

  getSuggestedIntakeDeltaPercent() {
    return this.getIntakeDeltaPercentForDedicationLevel(this.getSuggestedNutritionDedicationLevel());
  }

  private initializeCoachModeAsync() {
    const slot = this.coachClientSlot;

    // if re-editing, load existing data from the slot
    if (slot.intakeFormState) {
      this.intakeFormState = deepClone(slot.intakeFormState) as IntakeFormState;
      this.fitnessState = deepClone(slot.fitnessState) as FitnessState || this.userDb.getEmptyFitnessState();
      this.mode = 'change';
    } else {
      // fresh intake — use the same default factories as normal mode
      this.intakeFormState = this.userDb.getEmptyIntakeFormState();
      this.fitnessState = this.userDb.getEmptyFitnessState();
      this.mode = 'new';
    }
  }

  private async finishIntakeFormCoachModeAsync() {
    const loader = this.loadingCtrl.create({
      content: LL.intake.loading.finishingPlan(),
    });
    void loader.present();

    try {
      deepOverwriteCouchSafe(this.intakeFormState, this.generatedPlanIntakeFormState);

      // Propagate intakeFormState (weight_kg, bfData, exerciseStats) into
      // this.fitnessState. Without this the slot ships with weight_kg=undefined,
      // which the client picks up via deepOverwriteCouchSafe in coach-claim,
      // leaving managed clients stuck on "Unable to find any bodyweight." until
      // they log a fresh weigh-in. (Sentry CYBERNETIC-FITNESS-APP-369, #3286).
      this.fillFitnessState(this.fitnessState);

      // reload the slot from DB to get the latest _rev
      const slot = await this.userDb.database.getRecordAsync(this.coachClientSlot._id, this.docWatcher) as CoachClientSlot;
      slot.intakeFormState = deepClone(this.intakeFormState);
      // df-0fec46de9786: only store a program when there IS one. deepClone(undefined) returns
      // null, so an unguarded write would put null over whatever program the slot already held —
      // and this slot is the only place a client's program lives until they claim their invite,
      // where coach-claim.service.ts skips a falsy plan in silence. The plan can be empty on
      // purpose: somethingChanged() empties it when an answer change invalidates the program.
      // The ordinary user's finish already leaves the stored plan alone in that case (see
      // choosePlanToPersistAtFinish in intake-finish-plan.ts); this is the coach-side equivalent.
      // if there is a generated program, store it; otherwise keep the slot's own and report
      if (this.generatedFitnessPlan) {
        slot.fitnessPlan = deepClone(this.generatedFitnessPlan);
      } else {
        // an unresolved condition, not a repair: the coach reached Finish with nothing to save,
        // and without this report the only trace is a program that quietly did not change
        this.logging.trackSilentProblem(
          'intake: coach finish had no program to store',
          {
            mode: this.mode,
            currentView: this.currentView,
            slotAlreadyHeldPlan: !!slot.fitnessPlan,
            snapshotHeldPlan: !!this.intakeFormState.generatedFitnessPlan,
          });
      }
      slot.fitnessState = deepClone(this.fitnessState);
      await this.userDb.database.saveRecordAsync(slot, DatabaseWrapper.getTransactionId());
    } finally {
      void loader.dismiss();
    }
    await this.leaveFormAsync();
  }

  async finishIntakeForm(destination: 'home' | 'workout' = 'home') {
    // #4059: from here on THIS device owns navigation — tell the DocWatcher not to
    // auto-exit to Home when our own plan save flips fitnessPlan.ready (see the watcher).
    // if the finish fails mid-way, reset the flag so a stuck `true` can't permanently
    // disable that cross-device auto-exit; rethrow so the failure still surfaces.
    this.finishingIntake = true;
    // coach mode runs its own loader inside performFinishIntakeAsync; for the normal path
    // show the "finishing" spinner here so the finally always dismisses it — even if the
    // finish throws (otherwise the user is left on a stuck spinner over a dead page).
    const loader = this.isCoachMode ? null : this.loadingCtrl.create({ content: LL.intake.loading.finishingPlan() });
    if (loader) void loader.present();
    try {
      await this.performFinishIntakeAsync(destination);
    } catch (err) {
      this.finishingIntake = false;
      throw err;
    } finally {
      // #4193: surface a failed dismiss instead of silently void-ing it. A rejected
      // dismiss leaves the full-screen loading overlay (and its click-blocking backdrop)
      // up forever — which wedged the coach after a coached finalize. Per the
      // "unexpected conditions must surface" rule, report it rather than swallow it.
      if (loader) void loader.dismiss().catch(dismissErr => this.logging.trackSilentProblem('intake: finish loader dismiss failed', { err: String(dismissErr) }));
    }
  }

  // #4059: the real finish work, split out of finishIntakeForm so the wrapper can reset
  // the finishingIntake flag and dismiss the loader if this throws mid-way.
  private async performFinishIntakeAsync(destination: 'home' | 'workout') {
    // in coach mode, save to the client slot instead of the user's state
    if (this.isCoachMode) {
      await this.finishIntakeFormCoachModeAsync();
      return;
    }

    // option B coach review (#3389): capture BEFORE the normal finish
    // logic mutates welcomePhase. We use this AFTER plan activation to
    // notify the client that their program is ready and to flip the
    // coach's slot to Finalized via coachNotifyClientAsync. Capturing
    // up front keeps the flag stable across the awaited finish path.
    const wasCoachedFinalize = this.isCoachManaging()
      && (this.fitnessPlan?.welcomePhase === WelcomePhase.AwaitingCoachFinalize
          || this.fitnessPlan?.welcomePhase === WelcomePhase.CoachedIntake);

    // #4139: a coached finalize builds the client's FIRST plan from an empty pre-finalize
    // schedule, so it takes the fresh-default-schedule branch below (like mode==='new'),
    // never the adjust-existing branch. shouldReadjustDefaultSchedule is only consumed by
    // that adjust branch, and it reads the client's empty declaredState (trainingDays
    // undefined) which can throw — so skip the call for a coached finalize.
    const shouldReadjustDefaultSchedule = !wasCoachedFinalize && await this.shouldReadjustDefaultSchedule();

    deepOverwriteCouchSafe(this.intakeFormState, this.generatedPlanIntakeFormState);

    await Promise.all([this.fillAndSaveDeclaredState(), this.fillAndSaveImmutableState(), this.fillAndSaveFitnessState(), this.settings.saveAsync(), this.logIntakeFormAsync()]);

    this.queueClearingIntakeFormState = true;
    // replace plan if we're not skipping regeneration, OR if a nutrition-target-affecting
    // change re-solved the kept plan's nutrition that must now be persisted (#3382/#3635),
    // OR if the user edited the kept program from the overview (df-919e69164e24).
    if (!this.skipPlanRegeneration || this.nutritionResolvedForKeptPlan || this.planEditedForKeptPlan) {
      let transaction = DatabaseWrapper.getTransactionId();
      // #4660: persist the program the snapshot holds, not the separately-tracked field —
      // the document watcher can re-point that field at an arriving program, and a user
      // whose two copies had come apart had his rebuilt, hand-edited program silently
      // replaced by his old one. See intake-finish-plan.ts for the whole story.
      // intakeFormState was restored from the snapshot a few lines up, so it is the
      // same state the intake journal was just written from.
      const { plan: planToPersist, copiesDisagreed } =
        choosePlanToPersistAtFinish(this.generatedFitnessPlan, this.intakeFormState);
      if (copiesDisagreed) {
        // an unresolved condition, not a repair: the two copies should never differ, and
        // without this report the only trace of it is a plan that quietly did not change
        this.logging.trackSilentProblem(
          'intake: finish plan copies disagree',
          {
            transaction,
            tracked: describePlanForReport(this.generatedFitnessPlan),
            snapshot: describePlanForReport(planToPersist),
          });
      }
      if (!planToPersist && !this.skipPlanRegeneration) {
        // df-b9d12c28a387: the user asked for a new program and there is none to store, so the
        // write below changes nothing and their old program stays. what is saved is unchanged
        // here; the report is so this can no longer happen without anybody seeing it
        this.logging.trackSilentProblem('intake: finish after a rebuild has no program to save', {
          transaction,
          mode: this.mode,
          snapshot: describePlanForReport(this.intakeFormState?.generatedFitnessPlan),
        });
      }
      await this.svPlanHandler.replaceCurrentFitnessPlanAsync(transaction, planToPersist);
    }
    // #4139: coached finalize builds the client's first plan from a fresh default schedule,
    // same as a new user — the client's pre-finalize schedule is empty, and adjusting it
    // (the change branch below) would save NaN meal times into their account.
    if (this.mode === 'new' || wasCoachedFinalize) {
      const declaredState = await this.userDb.loadDeclaredStateAsync(this.docWatcher);
      const defaultSchedule = await this.svPlanHandler.generateDefaultScheduleAsync(declaredState);
      const schedule = await this.userDb.loadScheduleAsync();
      deepOverwriteCouchSafe(schedule, defaultSchedule);
      await this.userDb.saveScheduleAsync(DatabaseWrapper.getTransactionId());

      // track first scheduled workout date for email flow
      const firstWorkoutDate = getNextWorkoutDateISO(schedule.rules, this.debug.getNowLocal());
      if (firstWorkoutDate) {
        this.telemetry.setUserProperties({ first_scheduled_workout_at: firstWorkoutDate });
      }
    } else if (shouldReadjustDefaultSchedule) {
      const schedule = await this.userDb.loadScheduleAsync();
      const declareState = await this.userDb.loadDeclaredStateAsync(this.docWatcher);
      const ruleStartDate = this.debug.getNowLocal().toISODate();
      adjustScheduleToDeclaredState(schedule, declareState, ruleStartDate)
      await this.userDb.saveScheduleAsync(DatabaseWrapper.getTransactionId());

      // update first scheduled workout date for email flow
      const firstWorkoutDate = getNextWorkoutDateISO(schedule.rules, this.debug.getNowLocal());
      if (firstWorkoutDate) {
        this.telemetry.setUserProperties({ first_scheduled_workout_at: firstWorkoutDate });
      }
    }
    await this.userDb.clearBacklogCacheAsync();

    // now that the intake answers and the plan exist, refresh the Mixpanel profile.
    // the boot-time setUserInfo call ran before intake was done, so it could only
    // send the basic props (user_app_display_mode etc.) — without this second call,
    // a user whose whole trial happens in one session would never get the
    // intake-derived profile properties. skip when a coach is finishing a client's
    // intake: telemetry tracks the coach, not this account.
    if (!this.isCoachManaging()) {
      this.telemetry.setUserInfo(this.userDb, this.auth.user, this.settings);
    }

    // option B coach review finalize hook (#3389). Plan has been
    // activated inside the client's account; notify the client their
    // program is ready and flip the coach's slot to Finalized server-
    // side. Direct call, not the coach-queue — matches the
    // checkupResolved dispatch in weekly-checkup-page (await inside
    // try, surface a delivery failure via trackSilentProblem so a real
    // outage shows up in Sentry rather than dropping as an unhandled
    // rejection). The coach stays in the client's account after this
    // returns so they can add meal plan / pinned notes immediately.
    if (wasCoachedFinalize) {
      const managingClient = this.userDb.getCoachManagingClient();
      const relDoc = this.userDb.loadCoachRelAsClient();
      const clientUid = managingClient?.clientUid;
      const clientSlotId = relDoc?.clientSlotId;
      if (clientUid && clientSlotId) {
        try {
          const result = await this.cloudFunctions.coachNotifyClientAsync({
            type: 'programReady',
            clientUid,
            clientSlotId,
            coachDisplayName: this.auth.user?.name,
          });
          if (result.attempted && !result.emailSent) {
            this.logging.trackSilentProblem('coachNotifyClientEmailFailed', {
              type: 'programReady',
              emailError: result.emailError,
            });
          }
        } catch (err) {
          this.logging.trackSilentProblem('coachNotifyClientFailed', {
            type: 'programReady',
            err: describeErrorForLog(err),
          });
        }
      } else {
        // both are written at claim time and at managing-client setup;
        // surface so the cause is logged rather than silently dropping
        // the client notification.
        this.logging.trackSilentProblem('intake-form: coached finalize missing clientUid or clientSlotId', {
          hasClientUid: !!clientUid,
          hasClientSlotId: !!clientSlotId,
        });
      }
    }

    // in 'change' mode, go back to the previous page (Program Settings when the form is the
    // only page, see leaveFormAsync); in 'new' mode, reset to home.
    // #4193: a coached finalize (mode==='change' under coach impersonation) must NOT
    // pop. The page beneath the intake form is the client's page-awaiting-coach holding
    // page, which is now stale (activation flipped welcomePhase to Done). Nothing routes
    // the coach off it — that page has no plan watcher, and pageFilter does not run on a
    // pop reveal — so popping strands the coach on the locked "your coach is finalizing"
    // screen (and a racing loader dismiss could even wedge it behind the overlay). Route
    // into the client's Home instead (the coach stays impersonating), matching
    // coach-mode.md §8 ("the coach stays inside the client's account so they can
    // immediately add the meal plan and pinned notes") and reusing the new-user branch's
    // proven setRootAsync path.
    if (this.mode === 'change' && !wasCoachedFinalize) {
      await this.leaveFormAsync();
    } else {
      await this.navCtrl.setRootAsync(IHomePage, { tab: HomeTab.DailyLog });
      // #4059: "Start your first workout" (step 12) opens the first session right after
      // finishing, stacked over Home so Back returns there. Reuses the rest-day launch
      // path (a date-only workout the page builds from the next scheduled template).
      // Only when startable — otherwise the user just lands on Home like the old Finish.
      // #4193: skip for a coached finalize — this is the coach's device, not the client's,
      // so the client's first-workout handoff must not open on the coach's screen.
      if (!wasCoachedFinalize && destination === 'workout' && this.hasStartableFirstWorkout) {
        await this.navCtrl.pushAsync(IWorkoutDetailPage, { date: this.debug.getNowLocal().toISO() });
      }
      // request notification permission and schedule notifications for new users.
      // #4193: skip for a coached finalize — never request notification permission or
      // schedule the client's notifications from the coach's device.
      if (!wasCoachedFinalize && isCapacitorNative()) {
        void this.notificationService.requestPermissionAndScheduleAsync(
          this.userDb,
          this.settings,
          this.debug.getNowLocal(),
        );
      }
    }
  }

  handleEditTrainingProgram() {
    void this.editTrainingProgramAsync();
  }

  async editTrainingProgramAsync() {
    // load states with coach-mode scoping so Edit Workouts has correct data for
    // reoptimize and never inherits coach's own state in coach mode (#3289).
    const { declaredState, immutableState } = await this.loadStatesForGenerationAsync();

    // df-5556257f1229: remember where the fitness state stood before this page runs, so the
    // finish can tell what the user changed in there from what their intake answers changed.
    // Only on the FIRST visit — see the field's own comment for why a per-visit snapshot
    // would silently drop the earlier visits' edits.
    // (guarded on there being a state to copy as well, so a page that somehow reached here
    // without one leaves the field undefined and the finish behaves as it always did, rather
    // than recording a null that would stop a later visit taking a real starting point)
    if (this.fitnessStateBeforeEditWorkouts === undefined && this.fitnessState) {
      this.fitnessStateBeforeEditWorkouts = deepClone(this.fitnessState);
    }

    void this.navCtrl.pushAsync(IEditWorkoutsPage, {
      mode: 'intake',
      returnCallback: 'onTrainingProgramEdited',
      local: {
        fitnessPlan: this.generatedFitnessPlan,
        fitnessState: this.fitnessState,
        declaredState,
        immutableState,
      },
    });
  }

  async onTrainingProgramEdited(_context: unknown, result: EditWorkoutsPageResult) {
    // if user saved changes, update local data with returned values
    if (result) {
      // df-919e69164e24: read whether this really changed the program BEFORE the field moves
      const programChanged = !deepCompareEQ(this.generatedFitnessPlan, result.fitnessPlan);
      this.fitnessState = result.fitnessState;
      // update trainingData reference for refreshProgramOverviewAsync
      if (this.trainingData) {
        this.trainingData.fitnessPlan = result.fitnessPlan;
        this.trainingData.fitnessState = result.fitnessState;
      }
      // installing also refreshes the snapshot, so finishIntakeForm() doesn't revert workout edits
      this.installGeneratedPlan(result.fitnessPlan);
      if (programChanged) {
        this.planEditedForKeptPlan = true;
      }
    }
    // save the intake form document, which carries this edit until Finish applies it —
    // this does NOT write the user's program; only the finish does that
    this.somethingChanged();
    // refresh the program overview when user returns
    await this.refreshProgramOverviewAsync();
  }

  /**
   * Works out the nutrition card's figures for a plan and merges them onto `this.nutrition`.
   *
   * df-696b7c4e0aa0: every place on this page that refreshes the card needs the same long list
   * of inputs, and each one used to write that list out for itself. Several of those inputs are
   * optional, so a place missed while adding a new one says nothing at all — the object is built
   * inline and the field is simply left undefined. That already happened once, with the reporter
   * a failed solve is announced through. Assembling the list here is what stops these paths
   * drifting apart again.
   *
   * `program-settings-page` builds the same object for itself and is deliberately left alone: it
   * passes no intake-form state, takes its schedule rules from elsewhere, and replaces
   * `this.nutrition` outright instead of merging onto it.
   */
  private applyNutritionData(
    fitnessPlan: FitnessPlan,
    declaredState: DeclaredState,
    immutableState: ImmutableState,
    fitnessState: FitnessState,
    scheduleRules: ScheduleRule[],
  ): void {
    const nutritionData = computeNutritionPlanData({
      fitnessPlan,
      declaredState,
      immutableState,
      fitnessState,
      scheduleRules,
      intakeFormState: this.intakeFormState,
      today: this.debug.getNowLocal(),
      locale: this.settings.locale,
      reporter: this.logging
    });
    if (nutritionData) {
      this.nutrition = { ...this.nutrition, ...nutritionData } as any;
    }
  }

  private async refreshNutritionDataAsync() {
    if (!this.generatedFitnessPlan) return;
    const { declaredState, fitnessState, immutableState } = await this.loadStatesForGenerationAsync();
    const scheduleRules = await this.getScheduleRulesForPreviewAsync(declaredState);
    this.applyNutritionData(this.generatedFitnessPlan, declaredState, immutableState, fitnessState, scheduleRules);
    if (this.viewDestroyed) return;
    this.changeDetector.markForCheck();
  }

  /**
   * Carries the user's nutrition answer out onto a plan whose workouts are being kept
   * (the keep and volumes-only choices on the Update Your Program screen).
   *
   * Two different things can need doing, and they are independent:
   *
   * - **The daily calorie total.** When the user asked to update it AND something that
   *   actually determines it changed, the plan's nutrition baseline is marked stale so the
   *   solve works the number out again. When the user asked to keep it, or nothing that
   *   feeds it moved, the baseline is left alone and their number survives (#4973).
   * - **The split of that total across meals.** A change to how many meals a day the user
   *   eats always has to be re-solved, whichever answer they gave, or the plan holds
   *   per-meal targets for a meal count that no longer exists (#3382). This is why "keep my
   *   calorie target" means keeping the TOTAL, not leaving nutrition untouched.
   *
   * Marking the baseline stale also resets the nutrition progress anchor
   * (`nutritionAdjusted`), which is why it is not done for a meal-frequency-only change.
   *
   * Re-solving in place via updatePlanAsync recomputes the nutrition targets from the
   * in-progress declared state (intakeDeltaPercent / mealFrequency are projected on via
   * loadStatesForGenerationAsync -> fillDeclaredState) while preserving the workout
   * structure (PlanUpdate.solve reads but never writes defaultVolume_sets). Flags the plan
   * for persistence at finish (these paths otherwise skip the plan write).
   *
   * Coach mode is skipped: updatePlanAsync re-solves with the coach's own db/weigh-ins,
   * which would be wrong for a managed client's plan.
   */
  private async resolveNutritionForKeptPlanIfNeeded(nutritionChoice: NutritionUpdateChoice): Promise<void> {
    if (this.isCoachMode || !this.generatedFitnessPlan) return;
    const [appliedDeclaredState, appliedFitnessState] = await Promise.all([
      this.userDb.loadDeclaredStateAsync(this.docWatcher),
      this.userDb.loadFitnessStateAsync(this.docWatcher),
    ]);
    const mealFrequencyChanged = this.hasMealFrequencyChanged(appliedDeclaredState);
    const wantsFreshTarget = nutritionChoice === NutritionUpdateChoice.Update
      && (await this.hasTargetAffectingChangeAsync(appliedDeclaredState, appliedFitnessState));
    // A plan reaching here can still carry a stored mark: the Recalculate step used to put
    // one on the LIVE plan singleton (stopped in #4602), and plans saved by older builds keep
    // it. Keeping the target therefore has to CLEAR it, which means solving even when nothing
    // else needs re-solving — otherwise the mark rides into the next plan write and replaces
    // the target later, which is the very thing the user declined (#4973).
    const mustClearStaleMark = !wantsFreshTarget && this.generatedFitnessPlan.shouldInitializeBaselineAtNextReplan === true;
    if (!wantsFreshTarget && !mealFrequencyChanged && !mustClearStaleMark) return;
    const { declaredState, fitnessState, immutableState } = await this.loadStatesForGenerationAsync();
    // re-solve on a CLONE: on the keep path generatedFitnessPlan is the live plan singleton
    // (from loadPlanAsync; the volumes-only path clones it for itself, see #5381 in
    // applyUpdateChoicesAsync). The clone avoids mutating that live singleton
    // before the user confirms Finish (e.g. if they cancel from the overview) and keeps
    // generatedFitnessPlan distinct from the singleton, so replaceCurrentFitnessPlanAsync's
    // deepOverwriteCouchSafe at finish is a real write rather than a source===destination
    // no-op. (deepClone is already imported and used elsewhere in this file.)
    const replanned = deepClone(this.generatedFitnessPlan);
    // #3635: the daily calorie target lives in the plan's baseline
    // (nominalDailyAverageIntake), which updatePlanAsync's solve only recomputes when the
    // baseline is marked stale. Marking it is therefore what "update my calorie target"
    // means on a kept plan — and not marking it is what makes "keep" work.
    if (wantsFreshTarget) {
      // this path keeps the nutrition anchor, so the fresh target comes from the mark; the
      // discard stops #5181 putting a parked number back over it
      await this.svPlanHandler.markPlanForInitializeBaseline(replanned);
      await this.svPlanHandler.discardPendingCalorieTarget(replanned);
    } else {
      // the same helper the rebuild path uses, so "keep" means the same thing whichever
      // answer the user gave about their workouts
      this.svPlanHandler.keepNutritionTargetThroughNextSolve(replanned);
    }
    await this.svPlanHandler.updatePlanAsync(replanned, declaredState, fitnessState, immutableState);
    this.generatedFitnessPlan = replanned;
    this.nutritionResolvedForKeptPlan = true;
  }

  // df-0bfc5989154b: the page keeps the generated program in three places — this field,
  // intakeFormState.generatedFitnessPlan, and the snapshot the finish restores from. Every place
  // that installs a program the user should end up with writes all three through here, so none
  // can be left behind (#4660 was one copy left behind).
  // some writes deliberately touch fewer copies and must NOT call this: the document-watcher
  // re-link moves only the field (refreshing the snapshot there would persist the ARRIVING
  // program — #4660), the resets clear the field or field + state, and
  // resolveNutritionForKeptPlanIfNeeded moves only the field because its callers install after it.
  private installGeneratedPlan(plan: FitnessPlan) {
    this.generatedFitnessPlan = plan;
    this.intakeFormState.generatedFitnessPlan = plan;
    this.generatedPlanIntakeFormState = deepClone(this.intakeFormState);
  }

  async refreshProgramOverviewAsync() {
    if (this.trainingData) {
      this.programOverview = await this.workoutLoader.generateProgramOverviewAsync(this.trainingData, this.docWatcher);
      // set first workout as selected if not already set
      if (this.programOverview.workouts.list.length > 0 && !this.selectedWorkout) {
        this.selectedWorkout = this.programOverview.workouts.list[0].letter;
      }
      this.changeDetector.markForCheck();
    }
  }

  /**
   * Returns schedule rules appropriate for the nutrition card preview.
   * For a first-plan run, generates a default schedule from the declared state.
   * Otherwise loads the existing schedule and adjusts it to match the current
   * intake form state (preserving custom meal times).
   */
  private async getScheduleRulesForPreviewAsync(declaredState: DeclaredState) {
    // #4139: a first-plan run has no existing schedule to adjust — a pre-finalize client
    // never generated one — so build a fresh default schedule from scratch. Taking the
    // change branch below adjusts the client's empty schedule, which yields meal rules
    // with NaN times and makes the schedule/nutrition preview throw
    // ("Invalid unit value NaN").
    if (this.isBuildingFirstPlan()) {
      const defaultSchedule = await this.svPlanHandler.generateDefaultScheduleAsync(declaredState);
      return defaultSchedule.rules;
    }
    // for change mode, adjust the existing schedule to the current intake form state
    const schedule = deepClone(await this.userDb.loadScheduleAsync());
    const ruleStartDate = this.debug.getNowLocal().toISODate();
    adjustScheduleToDeclaredState(schedule, declaredState, ruleStartDate);
    return schedule.rules;
  }

  handleEditNutritionPlan() {
    void this.editNutritionPlanAsync();
  }

  async editNutritionPlanAsync() {
    const { declaredState, fitnessState, immutableState } = await this.loadStatesForGenerationAsync();
    const isFlexible = (this.intakeFormState?.macroTargets ?? declaredState.macroTargets) === MacroTargetsType.Flexible;
    const scheduleRules = await this.getScheduleRulesForPreviewAsync(declaredState);
    const today = this.debug.getNowLocal();
    const plan = this.generatedFitnessPlan;
    const overrides = plan.overrideMacros;

    // the editor needs both the user's current targets and the recommendation to open safely — it
    // decides what counts as one of their own overrides by comparing the two, and it saves what it
    // decides. Missing either means we do not open it at all (df-f3070b96b839).
    const macros = computeEditNutritionPlanMacros(
      plan, declaredState, immutableState, fitnessState, scheduleRules, today, isFlexible, this.logging);
    if (macros === null) {
      await this.showNutritionPlanUnavailableAlertAsync();
      return;
    }
    const { current, recommended } = macros;

    void this.navCtrl.pushAsync(IEditNutritionPlanPage, {
      returnCallback: 'onNutritionPlanEdited',
      local: {
        isFlexible,
        gender: this.intakeFormState.gender,
        mealsDaily: this.intakeFormState.mealFrequency,
        dailyIntake: plan.overrideDailyAverageIntake ?? plan.actualDailyAverageIntake ?? 0,
        protein: current.protein,
        carbs: current.carbs,
        fat: current.fat,
        fiber: current.fiber,
        recommendedDailyIntake: recommended.dailyIntake,
        recommendedProtein: recommended.protein,
        recommendedCarbs: recommended.carbs,
        recommendedFat: recommended.fat,
        recommendedFiber: recommended.fiber,
        carbFatRatio: overrides?.carbFatRatio,
        autoAdjust: overrides?.autoAdjust,
      },
    });
  }

  /**
   * Told when the nutrition editor cannot be opened because the numbers it needs could not be
   * worked out. The underlying failure has already been reported by whichever step could not
   * finish; this is what the user sees.
   */
  private async showNutritionPlanUnavailableAlertAsync() {
    await this.alertCtrl.showAlertAsync({
      message: LL.editNutritionPlan.couldNotComputeTargets(),
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
    });
  }

  /**
   * Callback invoked by NavController when returning from EditNutritionPlanPage.
   * This is called via the returnCallback mechanism in nav-controller.ts.
   */
  async onNutritionPlanEdited(_context: unknown, result: EditNutritionPlanPageResult | undefined) {
    // if user cancelled, nothing to update
    if (result === undefined) {
      return;
    }

    // if generatedFitnessPlan was cleared (e.g., by sync or page re-entry), nothing to update
    if (!this.generatedFitnessPlan) {
      return;
    }

    // df-919e69164e24: ask whether the USER changed anything, before the plan moves. Compare
    // against what the editor was OPENED with, not against the stored override fields — the
    // editor echoes its opening values straight back when the user types nothing, and the two
    // do not have the same shape. Comparing the whole plan after the re-solve below would be a
    // different question again ("did the solver produce identical output"), and a less stable
    // thing to hang a program write on.
    //
    // The daily figure: editNutritionPlanAsync opens the editor with
    // `overrideDailyAverageIntake ?? actualDailyAverageIntake ?? 0`, so a user who has never
    // set an override is shown a number while the field itself is undefined. Reading the field
    // here would call that a change for every such user, on every visit.
    const openedDailyIntake = this.generatedFitnessPlan.overrideDailyAverageIntake
      ?? this.generatedFitnessPlan.actualDailyAverageIntake ?? 0;
    // The macros: the editor rebuilds overrideMacros from scratch and returns NOTHING AT ALL
    // when no value differs from the recommendation (buildOverrideMacros ends in
    // `Object.keys(result).length > 0 ? result : undefined`). The stored field is likewise
    // absent for such a user. Both sides therefore need normalising before the comparison —
    // deepCompareEQ answers false for an absent value against an empty object, so dropping
    // either `?? {}` would make an untouched visit look like a change again.
    const macrosChanged = !deepCompareEQ(
      this.generatedFitnessPlan.overrideMacros ?? {}, result.overrideMacros ?? {});
    const overridesChanged = openedDailyIntake !== result.dailyIntake || macrosChanged;

    // df-919e69164e24: apply the overrides to a CLONE, never to the plan the page is holding.
    // Two reasons, and both bite. On the keep path generatedFitnessPlan IS the live plan
    // singleton (see the comment on the fresh-entry reset above; the volumes-only path clones
    // it for itself since #5381), so editing it
    // here would change the user's stored program in memory before they have pressed Finish —
    // and would leave replaceCurrentFitnessPlanAsync overwriting that object with itself.
    // Editing a clone and installing it also makes this callback match onTrainingProgramEdited,
    // which is what lets the snapshot refresh below be correct.
    // The plan the edit starts from is remembered so the guard further down can tell whether a
    // document arrived while we waited (#4660) — see design-docs/sync-integrity.md, "A fourth door".
    const planBeforeEdit = this.generatedFitnessPlan;
    const editedPlan = deepClone(planBeforeEdit);
    editedPlan.overrideDailyAverageIntake = result.dailyIntake;
    editedPlan.overrideMacros = result.overrideMacros;

    // recalculate plan with new override to update meal targets.
    // fill states with in-progress intake form values (DB may be stale during intake).
    // coach-mode scoping ensures we never re-introduce coach data here (#3289).
    const { declaredState, fitnessState, immutableState } = await this.loadStatesForGenerationAsync();
    await this.svPlanHandler.updatePlanAsync(editedPlan, declaredState, fitnessState, immutableState);

    // df-919e69164e24: install the edited program in all three places the page keeps it, then
    // refresh the snapshot. Without the snapshot refresh the finish restores intakeFormState
    // from a snapshot taken before this edit, and #4660 made the plan write read the snapshot's
    // copy — so on the REBUILD path the override was written away again and only the
    // 'intake: finish plan copies disagree' report showed it. onTrainingProgramEdited already
    // does exactly this; this callback was the one place that moved the field alone.
    //
    // All of it is skipped when the field no longer holds the plan this edit started from: the
    // awaits above let a document arrive and the watcher re-point the field at it. Installing
    // over that would discard what arrived, and refreshing the snapshot from it would hide the
    // 'finish plan copies disagree' report that exists to catch exactly this. Explained in
    // design-docs/sync-integrity.md, "A fourth door".
    if (this.generatedFitnessPlan === planBeforeEdit) {
      if (this.trainingData) {
        this.trainingData.fitnessPlan = editedPlan;
      }
      this.installGeneratedPlan(editedPlan);
    }
    if (overridesChanged) {
      this.planEditedForKeptPlan = true;
    }

    // update nutrition data after recalculation
    const scheduleRules = await this.getScheduleRulesForPreviewAsync(declaredState);
    this.applyNutritionData(this.generatedFitnessPlan, declaredState, immutableState, fitnessState, scheduleRules);

    // save the intake form document, which carries these overrides until Finish applies them —
    // this does NOT write the user's program; only the finish does that
    this.somethingChanged();
  }

  getWorkoutCount(): string {
    if (this.generatedFitnessPlan && this.generatedFitnessPlan.workouts) {
      return this.generatedFitnessPlan.workouts.length.toString();
    }
    return '?';
  }

  handleIntakeDeltaPercentSliderValueChange() {
    this.somethingChanged();
    this.updateIntakeDeltaBadgeColor();
    this.changeDetector.markForCheck();
  }

  handleTrainingVolumeWeeklyAvgSliderValueChange() {
    this.somethingChanged();
    this.updateTrainingVolumeBadgeColor();
    this.changeDetector.markForCheck();
  }

  // calculate interpolated color for training volume badge based on gradient marks
  private interpolateColor(color1: string, color2: string, ratio: number): string {
    const parseColor = (color: string) => {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 1;
      const ctx = canvas.getContext('2d');
      if (typeof ctx.fillStyle !== 'string') {
        throw new Error("Invalid color format or unsupported fillStyle.");
      }
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, 1, 1);
      const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
      return { r, g, b, a: a / 255 };
    };

    const c1 = parseColor(color1);
    const c2 = parseColor(color2);

    const r = Math.round(c1.r + (c2.r - c1.r) * ratio);
    const g = Math.round(c1.g + (c2.g - c1.g) * ratio);
    const b = Math.round(c1.b + (c2.b - c1.b) * ratio);

    return `rgb(${r}, ${g}, ${b})`;
  }

  private findInterpolatedColorForTrainingVolume(value: number): string {
    const gradientMarks = this.trainingVolumeSliderTrackBackgroundGradientMarks;
    if (gradientMarks.length === 0) {
      return '';
    }

    // get CSS variable values for colors from the slider element
    const sliderElement = document.querySelector('#trainingVolumeWeeklyAvgSlider');
    if (!sliderElement) {
      return '';
    }

    const getCSSColor = (colorName: string) => {
      return getComputedStyle(sliderElement).getPropertyValue(`--${colorName}`).trim();
    };

    let interpolatedColor = '';
    if (value >= gradientMarks[gradientMarks.length - 1].value) {
      interpolatedColor = getCSSColor(gradientMarks[gradientMarks.length - 1].color);
    } else if (value <= gradientMarks[0].value) {
      interpolatedColor = getCSSColor(gradientMarks[0].color);
    } else {
      for (let i = 1; i < gradientMarks.length; i++) {
        if (value >= gradientMarks[i - 1].value && value < gradientMarks[i].value) {
          const color1 = getCSSColor(gradientMarks[i - 1].color);
          const color2 = getCSSColor(gradientMarks[i].color);
          interpolatedColor = this.interpolateColor(color1, color2, (value - gradientMarks[i - 1].value) / (gradientMarks[i].value - gradientMarks[i - 1].value));
          break;
        }
      }
    }

    return interpolatedColor;
  }

  private updateTrainingVolumeBadgeColor() {
    if (!this.trainingVolumeBadgeValue || this.trainingVolumeSliderTrackBackgroundGradientMarks.length === 0) {
      return;
    }

    const color = this.findInterpolatedColorForTrainingVolume(this.intakeFormState.trainingVolumeWeeklyAvg);
    if (color) {
      const element = this.trainingVolumeBadgeValue.nativeElement as HTMLElement;
      element.style.setProperty('color', color);
    }
  }

  private findInterpolatedColorForIntakeDelta(value: number): string {
    const gradientMarks = this.intakeDeltaPercentSliderTrackBackgroundGradientMarks;
    if (gradientMarks.length === 0) {
      return '';
    }

    // get CSS variable values for colors from the slider element
    const sliderElement = document.querySelector('#intakeDeltaPercentSlider');
    if (!sliderElement) {
      return '';
    }

    const getCSSColor = (colorName: string) => {
      return getComputedStyle(sliderElement).getPropertyValue(`--${colorName}`).trim();
    };

    let interpolatedColor = '';
    if (value >= gradientMarks[gradientMarks.length - 1].value) {
      interpolatedColor = getCSSColor(gradientMarks[gradientMarks.length - 1].color);
    } else if (value <= gradientMarks[0].value) {
      interpolatedColor = getCSSColor(gradientMarks[0].color);
    } else {
      for (let i = 1; i < gradientMarks.length; i++) {
        if (value >= gradientMarks[i - 1].value && value < gradientMarks[i].value) {
          const color1 = getCSSColor(gradientMarks[i - 1].color);
          const color2 = getCSSColor(gradientMarks[i].color);
          interpolatedColor = this.interpolateColor(color1, color2, (value - gradientMarks[i - 1].value) / (gradientMarks[i].value - gradientMarks[i - 1].value));
          break;
        }
      }
    }

    return interpolatedColor;
  }

  private updateIntakeDeltaBadgeColor() {
    if (!this.intakeDeltaBadgeValue || this.intakeDeltaPercentSliderTrackBackgroundGradientMarks.length === 0) {
      return;
    }

    const color = this.findInterpolatedColorForIntakeDelta(this.intakeFormState.intakeDeltaPercent);
    if (color) {
      const element = this.intakeDeltaBadgeValue.nativeElement as HTMLElement;
      element.style.setProperty('color', color);
    }
  }
}
